// src/firmsData.js
export const C = {
  bg: "#0d1117", surface: "#161b22", card: "#1c2128", border: "#30363d",
  borderLight: "#21262d", blue: "#3b82f6", blueHover: "#2563eb", blueDim: "#1e3a5f",
  text: "#e6edf3", textMuted: "#8b949e", textDim: "#6e7681",
  red: "#f85149", redBg: "#2d1b1b", redBorder: "#6e2c2c",
  orange: "#f0883e", orangeBg: "#2d1e0e", orangeBorder: "#6e3c10",
  yellow: "#d29922", yellowBg: "#2a1f00", yellowBorder: "#6e4f00",
  green: "#3fb950", greenBg: "#0d2318", greenBorder: "#1a4d2b", purple: "#60a5fa",
  blueBg: "#0d1e35", blueBorder: "#1e3a5f",
};

/** Alpha Futures: all "Visit Firm" links use this URL. Override with REACT_APP_ALPHA_VISIT_HREF if needed. */
const ALPHA_AFFILIATE_VISIT_URL =
  (typeof process !== "undefined" && process.env.REACT_APP_ALPHA_VISIT_HREF) ||
  "https://app.alpha-futures.com/signup/Jonah020857/";

/** Top One Futures: all "Visit Firm" links use this URL. Override with REACT_APP_TOPONE_VISIT_HREF if needed. */
const TOPONE_AFFILIATE_VISIT_URL =
  (typeof process !== "undefined" && process.env.REACT_APP_TOPONE_VISIT_HREF) ||
  "https://toponefutures.com/?linkId=lp_707970&sourceId=atlas&tenantId=toponefutures";

export function getFirmVisitHref(firm) {
  if (!firm) return null;
  if (firm.id === "alpha") return ALPHA_AFFILIATE_VISIT_URL;
  if (firm.id === "topone") return TOPONE_AFFILIATE_VISIT_URL;
  if (firm.website) return `https://${firm.website}`;
  return null;
}

/* ──────────────── TOPSTEP DATA CONSTANTS ──────────────── */

const TS_PRICING = [
  { label: "Monthly (w/ activation)", values: ["$49/mo", "$99/mo", "$149/mo"] },
  { label: "Activation fee", values: ["$149", "$149", "$149"] },
  { label: "Monthly (no activation)", values: ["$109/mo", "$159/mo", "$209/mo"] },
  { label: "Reset fee", values: ["$49", "$99", "$149"] },
];

const TS_EVAL = [
  { label: "Profit target", values: ["$3,000", "$6,000", "$9,000"], color: C.green },
  { label: "Max loss (MLL)", values: ["$2,000", "$3,000", "$4,500"], color: C.red },
  { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
  { label: "Max position", values: ["5 Minis", "10 Minis", "15 Minis"], color: null },
  { label: "Consistency rule", values: ["50%", "50%", "50%"], color: null },
  { label: "Min trading days", values: ["2", "2", "2"], color: null },
  { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
  { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
];

const TS_XFA_STANDARD = [
  { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
  { label: "Max loss (MLL)", values: ["$2,000", "$3,000", "$4,500"], color: C.red },
  { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
  { label: "Max position", values: ["5 Minis", "10 Minis", "15 Minis"], color: null },
  { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
  { label: "Winning days req.", values: ["5 × $150+", "5 × $150+", "5 × $150+"], color: null },
  { label: "Max payout", values: ["$5,000", "$5,000", "$5,000"], color: C.green },
  { label: "Profit split", values: ["90%", "90%", "90%"], color: C.green },
];

const TS_XFA_CONSISTENCY = [
  { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
  { label: "Max loss (MLL)", values: ["$2,000", "$3,000", "$4,500"], color: C.red },
  { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
  { label: "Max position", values: ["5 Minis", "10 Minis", "15 Minis"], color: null },
  { label: "Consistency rule", values: ["40%", "40%", "40%"], color: null },
  { label: "Qualifying days", values: ["3", "3", "3"], color: null },
  { label: "Max payout", values: ["$6,000", "$6,000", "$6,000"], color: C.green },
  { label: "Profit split", values: ["90%", "90%", "90%"], color: C.green },
];

const TS_SECTIONS_STANDARD = [
  { title: "Account Info", dot: C.textDim, items: [
    { bold: "Drawdown Behavior", lines: [
      "EOD trailing MLL — recalculates once per day at end-of-session using closing balance.",
      "No Daily Loss Limit on TopstepX.",
    ]},
    { bold: "Contract Scaling", lines: [
      "XFA uses a Scaling Plan — contract limits start lower and increase as profit grows.",
      "Maximum 5 XFAs active simultaneously.",
    ]},
    { bold: "Account Limits", lines: [
      "XFA starts at $0 balance — buying power matches your Combine account size.",
      "Monthly subscription ends after passing — no monthly fee on XFA.",
      "30 days of inactivity may close the XFA.",
    ]},
    { bold: "Unique Features", lines: [
      "NFA registered (TopStep Brokerage LLC).",
      "Back2Funded: if XFA breaches, reactivate up to 2 times (same size, same payout policy).",
    ]},
  ]},
  { title: "Trading Restrictions", dot: C.orange, items: [
    { bold: "News Trading", lines: [
      "No specific news trading restriction — standard session rules apply.",
    ]},
    { bold: "Prohibited Strategies", lines: [
      "No AI or automated trading outside the TopstepX API.",
      "No exploiting simulated fills or unrealistic execution.",
      "No VPNs, proxies, TOR, or geo-masking.",
    ]},
    { bold: "Position Rules", lines: [
      "All positions must be closed by 3:10 PM CT.",
      "No overnight or weekend positions.",
      "Do not hold positions within 2% of a contract's daily price limit.",
      "Copy trading allowed between Combine and XFA only.",
    ]},
    { bold: "Market Rules", lines: [
      "No hedging across accounts.",
      "No account stacking or trading on behalf of others.",
    ]},
  ]},
  { title: "Payout Rules", dot: C.green, items: [
    { bold: "Profit Split", lines: [
      "90/10 split (traders joining after Jan 12, 2026).",
      "Legacy traders: first $10K at 100%, then 90/10.",
    ]},
    { bold: "Schedule & Requirements", lines: [
      "5 winning days of $150+ required per payout cycle.",
      "Must be profitable since last payout (except first payout).",
      "After payout: MLL resets to $0 and winning day count restarts.",
    ]},
    { bold: "Limits", lines: [
      "Maximum payout: 50% of account balance, capped at $5,000.",
      "Minimum payout: $125.",
    ]},
    { bold: "Processing", lines: [
      "Payout approval: 1–3 business days. Funds arrival: up to 10 business days.",
      "Wise payouts arrive within 1 business day ($0.39 fee USD to USD).",
      "ACH/Wire: $30 processing fee.",
    ]},
  ]},
  { title: "Account Killers", dot: C.orange, items: [
    { bold: "Instant Breach", lines: [
      "Balance touching or falling below the Maximum Loss Limit.",
      "Holding positions past 3:10 PM CT.",
      "Account balance reaching $0 after a payout.",
    ]},
    { bold: "Inactivity", lines: [
      "30+ days with no trades — XFA may be closed.",
    ]},
    { bold: "Rule Violations", lines: [
      "Using VPNs or geo-masking.",
      "Manipulating simulated trading conditions.",
      "Account stacking or trading for others.",
    ]},
  ]},
];

const TS_SECTIONS_CONSISTENCY = [
  { title: "Account Info", dot: C.textDim, items: [
    { bold: "Drawdown Behavior", lines: [
      "EOD trailing MLL — recalculates once per day at end-of-session using closing balance.",
      "No Daily Loss Limit on TopstepX.",
    ]},
    { bold: "Consistency Rule", lines: [
      "40% consistency rule applies on XFA — resets after each payout.",
      "No single day's profit can exceed 40% of total profits to qualify for payout.",
    ]},
    { bold: "Contract Scaling", lines: [
      "XFA uses a Scaling Plan — contract limits start lower and increase as profit grows.",
      "Maximum 5 XFAs active simultaneously.",
    ]},
    { bold: "Account Limits", lines: [
      "XFA starts at $0 balance — buying power matches your Combine account size.",
      "Monthly subscription ends after passing — no monthly fee on XFA.",
      "30 days of inactivity may close the XFA.",
    ]},
    { bold: "Unique Features", lines: [
      "NFA registered (TopStep Brokerage LLC).",
      "Back2Funded: if XFA breaches, reactivate up to 2 times (same size, same payout policy).",
    ]},
  ]},
  { title: "Trading Restrictions", dot: C.orange, items: [
    { bold: "News Trading", lines: [
      "No specific news trading restriction — standard session rules apply.",
    ]},
    { bold: "Prohibited Strategies", lines: [
      "No exploiting simulated fills or unrealistic execution.",
      "No VPNs, proxies, TOR, or geo-masking.",
    ]},
    { bold: "Position Rules", lines: [
      "All positions must be closed by 3:10 PM CT.",
      "No overnight or weekend positions.",
      "Copy trading allowed between Combine and XFA only.",
    ]},
    { bold: "Market Rules", lines: [
      "No hedging across accounts.",
      "No account stacking or trading on behalf of others.",
    ]},
  ]},
  { title: "Payout Rules", dot: C.green, items: [
    { bold: "Profit Split", lines: [
      "90/10 split (traders joining after Jan 12, 2026).",
      "Legacy traders: first $10K at 100%, then 90/10.",
    ]},
    { bold: "Schedule & Requirements", lines: [
      "3 qualifying trading days required per payout cycle.",
      "40% consistency rule must be satisfied — no single day can exceed 40% of total profits.",
      "Consistency count resets to 0 after each payout.",
      "After payout: MLL resets to $0.",
    ]},
    { bold: "Limits", lines: [
      "Maximum payout: 50% of account balance, capped at $6,000.",
      "Minimum payout: $125.",
    ]},
    { bold: "Processing", lines: [
      "Payout approval: 1–3 business days. Funds arrival: up to 10 business days.",
      "Wise payouts arrive within 1 business day ($0.39 fee USD to USD).",
      "ACH/Wire: $30 processing fee.",
    ]},
  ]},
  { title: "Account Killers", dot: C.orange, items: [
    { bold: "Instant Breach", lines: [
      "Balance touching or falling below the MLL.",
      "Holding positions past 3:10 PM CT.",
      "Account balance reaching $0 after a payout.",
    ]},
    { bold: "Inactivity", lines: [
      "30+ days with no trades — XFA may be closed.",
    ]},
    { bold: "Rule Violations", lines: [
      "Using VPNs or geo-masking.",
      "Account stacking or manipulative trading behavior.",
    ]},
  ]},
];

export const FIRMS = [
  {
    id: "Topstep", name: "Topstep", tagline: "The original. NFA regulated. EOD trailing on TopstepX.",
    bestFor: "Traders who want NFA regulation and EOD-only drawdown with no Daily Loss Limit.",
    logo: "https://www.google.com/s2/favicons?domain=topstep.com&sz=64",
    founded: "2012", location: "Chicago, IL", regulated: true, regulator: "NFA Registered",
    website: "topstep.com", drawdownType: "EOD Trailing", profitSplit: "90%",
    newsTrading: true, overnightAllowed: false, hasDLL: false, maxFundedAccts: "5 XFAs",
    maxPayout: "$6,000 (Consistency)", daysToPass: "2 days",
    platforms: ["TopstepX"], trustpilotRating: 3.4,
    products: [
      {
        name: "XFA Standard", badge: "5 winning days · $150+ per day",
        params: [["Profit Target","$3K / $6K / $9K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None (TopstepX)"],["Consistency Rule","50% eval / None XFA"],["Max Position Size","Scaling Plan (up to 5 / 10 / 15 minis)"],["Min Trading Days","2 eval / 5 winning days XFA"]],
        sizes: ["50K", "100K", "150K"],
        pricing: TS_PRICING,
        evalTable: { title: "Evaluation (Combine)", rows: TS_EVAL },
        fundedTable: { title: "XFA (Express Funded) — Standard", rows: TS_XFA_STANDARD },
        sections: TS_SECTIONS_STANDARD,
      },
      {
        name: "XFA Consistency", badge: "3 days + 40% rule · $6K cap",
        params: [["Profit Target","$3K / $6K / $9K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None (TopstepX)"],["Consistency Rule","50% eval / 40% XFA"],["Max Position Size","Scaling Plan (up to 5 / 10 / 15 minis)"],["Min Trading Days","2 eval / 3 qualifying days XFA"]],
        sizes: ["50K", "100K", "150K"],
        pricing: TS_PRICING,
        evalTable: { title: "Evaluation (Combine)", rows: TS_EVAL },
        fundedTable: { title: "XFA (Express Funded) — Consistency", rows: TS_XFA_CONSISTENCY },
        sections: TS_SECTIONS_CONSISTENCY,
      },
    ],
  },
  {
    id: "apex", name: "Apex Trader Funding", tagline: "4.0 overhaul March 2026. One-time fees. EOD or Intraday drawdown. 100% split.",
    bestFor: "Traders who want 100% split, EOD or intraday drawdown choice, and up to 20 PAs.",
    logo: "https://www.google.com/s2/favicons?domain=apextraderfunding.com&sz=64",
    founded: "2021", location: "Austin, TX", regulated: false, website: "apextraderfunding.com",
    drawdownType: "EOD or Intraday Trailing (your choice)", profitSplit: "100%",
    newsTrading: true, overnightAllowed: false, hasDLL: true, maxFundedAccts: "20 PAs",
    maxPayout: "$5,000 (150K step 6)", daysToPass: "1 day",
    platforms: ["NinjaTrader", "Tradovate", "Rithmic", "WealthCharts"], trustpilotRating: 4.4,
    products: [
      {
        name: "EOD Account", badge: "100% split · EOD trailing · DLL protection",
        params: [["Profit Target","$1.5K / $3K / $6K / $9K"],["Max Drawdown","$1K / $2K / $3K / $4K EOD Trailing"],["Daily Loss Limit","$500 / $1K / $1.5K / $2K by size"],["Consistency Rule","None eval / 50% PA"],["Max Position Size","4 / 6 / 8 / 12 eval · 2 / 4 / 6 / 10 PA"],["Min Trading Days","None eval / 5 qualifying days PA"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "One-time eval fee", values: ["$199", "$249", "$349", "$799"] },
          { label: "Typical promo (~80% off)", values: ["~$40", "~$50", "~$70", "~$160"] },
          { label: "PA activation fee", values: ["$99", "$99", "$99", "$99"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (EOD)", values: ["$1,000", "$2,000", "$3,000", "$4,000"], color: C.red },
            { label: "Daily loss limit", values: ["$500", "$1,000", "$1,500", "$2,000"], color: null },
            { label: "Max contracts", values: ["4", "6", "8", "12"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["30 days", "30 days", "30 days", "30 days"], color: null },
          ],
        },
        fundedTable: {
          title: "Performance Account (PA)",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (EOD)", values: ["$1,000", "$2,000", "$3,000", "$4,000"], color: C.red },
            { label: "Daily loss limit", values: ["Tier-based", "Tier-based", "Tier-based", "Tier-based"], color: null },
            { label: "Max contracts", values: ["2", "4", "6", "10"], color: null },
            { label: "Consistency rule", values: ["50%", "50%", "50%", "50%"], color: null },
            { label: "Qualifying day min", values: ["5 x $100+", "5 x $250+", "5 x $250+", "5 x $300+"], color: null },
            { label: "Safety Net", values: ["$26,100", "$52,100", "$103,100", "$154,100"], color: null },
            { label: "Max payout", values: ["$1,000 (tiered)", "$1,500 (tiered)", "$2,000 (tiered)", "$2,500 (tiered)"], color: C.green },
            { label: "Profit split", values: ["100%", "100%", "100%", "100%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — threshold recalculated once per day at 4:59:59 PM ET using end-of-day closing balance.",
              "Threshold trails upward with new closing-balance highs and never moves down.",
              "On the PA, trailing stops once the threshold reaches Safety Net (starting balance + $100) — floor is then fixed permanently.",
            ]},
            { bold: "Daily Loss Guard", lines: [
              "PA DLL pauses trading for the session — does NOT fail the account. Trading day resets at 6:00 PM ET.",
            ]},
            { bold: "Tier-Based DLL & Contract Scaling", lines: [
              "Max contracts and DLL both scale with cumulative PA profit. Higher profit = more contracts and a larger DLL buffer.",
            ]},
            { bold: "25K PA", lines: [
              "Lv 1 · $0–$999 → 1 contract · $500 DLL",
              "Lv 2 · $1,000–$1,999 → 2 contracts · $500 DLL",
              "Lv 3 · $2,000+ → 2 contracts · $1,250 DLL",
            ]},
            { bold: "50K PA", lines: [
              "Lv 1 · $0–$1,499 → 2 contracts · $1,000 DLL",
              "Lv 2 · $1,500–$2,999 → 3 contracts · $1,000 DLL",
              "Lv 3 · $3,000–$5,999 → 4 contracts · $2,000 DLL",
              "Lv 4 · $6,000+ → 4 contracts · $3,000 DLL",
            ]},
            { bold: "100K PA", lines: [
              "Lv 1 · $0–$1,999 → 3 contracts · $1,750 DLL",
              "Lv 2 · $2,000–$2,999 → 4 contracts · $1,750 DLL",
              "Lv 3 · $3,000–$4,999 → 5 contracts · $1,750 DLL",
              "Lv 4 · $5,000–$9,999 → 6 contracts · $2,500 DLL",
              "Lv 5 · $10,000+ → 6 contracts · $3,500 DLL",
            ]},
            { bold: "150K PA", lines: [
              "Lv 1 · $0–$1,999 → 4 contracts · $2,500 DLL",
              "Lv 2 · $2,000–$2,999 → 5 contracts · $2,500 DLL",
              "Lv 3 · $3,000–$4,999 → 7 contracts · $2,500 DLL",
              "Lv 4 · $5,000–$9,999 → 10 contracts · $3,000 DLL",
              "Lv 5 · $10,000+ → 10 contracts · $4,000 DLL",
            ]},
            { bold: "Account Limits", lines: [
              "One-time eval purchase — no recurring monthly subscription under 4.0.",
              "Evaluation period: 30 calendar days from purchase. No extensions, no resets within the window.",
              "Evaluation reviewed at market close — officially marked passed after 6:00 PM ET.",
              "7 calendar days to activate the PA after passing — failing to activate forfeits the passed eval.",
              "PA creation can take up to 6 hours after activation payment.",
              "Max 20 PAs simultaneously across all Apex account types (EOD + Intraday combined).",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading allowed for normal strategy execution.",
              "Using 'news trading' strategies that chase the market or gamble outcomes with orders on both sides is prohibited.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No fully automated or hands-off trading systems — traders must remain actively involved.",
              "Using the full drawdown threshold as a stop-loss is prohibited.",
              "Cannot hedge evaluations against each other — each eval must be completed with independent directional trading.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 4:59 PM ET — no holding through session breaks.",
              "Copy trading allowed across PA accounts under the same name.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts or correlated instruments.",
              "Metals futures (GC, SI, MGC, HG, PL, PA) suspended as of March 2026 — no return date announced.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "100% of approved payout amount.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "5 qualifying trading days required per payout cycle.",
              "50% consistency rule must be satisfied to request payout.",
              "Balance must remain above Safety Net after withdrawal.",
              "Trading may continue after submitting a payout request.",
            ]},
            { bold: "Limits", lines: [
              "6-payout ladder per PA — cap increases with each successful withdrawal. After 6 payouts, the PA closes.",
              "Payout caps by step — 25K: $1,000 (all 6). 50K: $1,500 / $1,500 / $2,000 / $2,500 / $2,500 / $3,000. 100K: $2,000 / $2,500 / $2,500 / $3,000 / $4,000 / $4,000. 150K: $2,500 / $3,000 / $3,000 / $3,000 / $4,000 / $5,000.",
              "Minimum payout: $500.",
            ]},
            { bold: "Processing", lines: [
              "Processed via Deel — automated, no manual review.",
              "Typically 24–48 hours review; funds arrive in 3–7 business days.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below the EOD drawdown threshold — immediate liquidation and account failure.",
              "Dropping below the Safety Net after submitting a payout request — payout denied automatically.",
            ]},
            { bold: "Inactivity", lines: [
              "Failing to activate the PA within 7 calendar days after passing the eval — eval is forfeited.",
              "Must have 2 trading days with $50+ net profit within every rolling 30-day period or PA may close.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using the drawdown threshold as a stop-loss mechanism.",
              "Hedging across accounts or correlated instruments.",
              "Fully automated or hands-off trading.",
              "Repeated or blatant news trading violations on the PA.",
            ]},
          ]},
        ],
      },
      {
        name: "Intraday Trail Account", badge: "100% split · Real-time trailing · No DLL in eval",
        params: [["Profit Target","$1.5K / $3K / $6K / $9K"],["Max Drawdown","$1K / $2K / $3K / $4K Intraday Trailing"],["Daily Loss Limit","None eval / Tier-based PA"],["Consistency Rule","None eval / 50% PA"],["Max Position Size","4 / 6 / 8 / 12 eval · 2 / 4 / 6 / 10 PA"],["Min Trading Days","None eval / 5 qualifying days PA"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "One-time eval fee", values: ["$118", "$131", "$198", "$265"] },
          { label: "Typical promo (~80% off)", values: ["~$24", "~$26", "~$40", "~$53"] },
          { label: "PA activation fee", values: ["$79", "$79", "$79", "$79"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (Intraday)", values: ["$1,000", "$2,000", "$3,000", "$4,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["4", "6", "8", "12"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["30 days", "30 days", "30 days", "30 days"], color: null },
          ],
        },
        fundedTable: {
          title: "Performance Account (PA)",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (Intraday)", values: ["$1,000", "$2,000", "$3,000", "$4,000"], color: C.red },
            { label: "Daily loss limit", values: ["Tier-based", "Tier-based", "Tier-based", "Tier-based"], color: null },
            { label: "Max contracts", values: ["2", "4", "6", "10"], color: null },
            { label: "Consistency rule", values: ["50%", "50%", "50%", "50%"], color: null },
            { label: "Qualifying day min", values: ["5 x $100+", "5 x $200+", "5 x $250+", "5 x $300+"], color: null },
            { label: "Safety Net", values: ["$26,100", "$52,100", "$103,100", "$154,100"], color: null },
            { label: "Max payout", values: ["$1,000 (tiered)", "$1,500 (tiered)", "$2,000 (tiered)", "$2,500 (tiered)"], color: C.green },
            { label: "Profit split", values: ["100%", "100%", "100%", "100%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "Intraday trailing drawdown — threshold trails in real time with highest account balance, including unrealized profit.",
              "Threshold moves up tick-by-tick with peak equity and never moves down.",
              "A single equity spike followed by a reversal can fail the account even if the day ends flat — unrealized highs raise the floor permanently.",
              "On the PA, trailing stops once threshold reaches Safety Net (starting balance + $100) — floor is then fixed permanently.",
            ]},
            { bold: "Daily Loss Guard", lines: [
              "No DLL during evaluation — the trailing threshold is the only floor.",
              "In the PA, a tier-based DLL is enforced intraday — pauses trading, does NOT fail the account.",
            ]},
            { bold: "Tier-Based DLL & Contract Scaling", lines: [
              "Max contracts and DLL both scale with cumulative PA profit. Higher profit = more contracts and a larger DLL buffer.",
            ]},
            { bold: "25K PA", lines: [
              "Lv 1 · $0–$999 → 1 contract · $500 DLL",
              "Lv 2 · $1,000–$1,999 → 2 contracts · $500 DLL",
              "Lv 3 · $2,000+ → 2 contracts · $1,250 DLL",
            ]},
            { bold: "50K PA", lines: [
              "Lv 1 · $0–$1,499 → 2 contracts · $1,000 DLL",
              "Lv 2 · $1,500–$2,999 → 3 contracts · $1,000 DLL",
              "Lv 3 · $3,000–$5,999 → 4 contracts · $2,000 DLL",
              "Lv 4 · $6,000+ → 4 contracts · $3,000 DLL",
            ]},
            { bold: "100K PA", lines: [
              "Lv 1 · $0–$1,999 → 3 contracts · $1,750 DLL",
              "Lv 2 · $2,000–$2,999 → 4 contracts · $1,750 DLL",
              "Lv 3 · $3,000–$4,999 → 5 contracts · $1,750 DLL",
              "Lv 4 · $5,000–$9,999 → 6 contracts · $2,500 DLL",
              "Lv 5 · $10,000+ → 6 contracts · $3,500 DLL",
            ]},
            { bold: "150K PA", lines: [
              "Lv 1 · $0–$1,999 → 4 contracts · $2,500 DLL",
              "Lv 2 · $2,000–$2,999 → 5 contracts · $2,500 DLL",
              "Lv 3 · $3,000–$4,999 → 7 contracts · $2,500 DLL",
              "Lv 4 · $5,000–$9,999 → 10 contracts · $3,000 DLL",
              "Lv 5 · $10,000+ → 10 contracts · $4,000 DLL",
            ]},
            { bold: "Account Limits", lines: [
              "One-time eval purchase — no recurring monthly subscription under 4.0.",
              "Evaluation period: 30 calendar days from purchase. No extensions, no resets.",
              "7 calendar days to activate the PA after passing — failing to activate forfeits the passed eval.",
              "PA creation can take up to 6 hours after activation payment.",
              "Max 20 PAs simultaneously across all Apex account types (EOD + Intraday combined).",
            ]},
            { bold: "Unique Features", lines: [
              "Inactivity rule: must have 2 trading days with $50+ net profit within every rolling 30-day period or PA may close.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading allowed for normal strategy execution.",
              "Using 'news trading' strategies that chase the market or gamble outcomes with orders on both sides is prohibited.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No fully automated or hands-off trading systems — traders must remain actively involved.",
              "Using the full trailing threshold as a stop-loss is prohibited.",
              "Cannot hedge evaluations against each other.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 4:59 PM ET — no holding through session breaks.",
              "Copy trading allowed across PA accounts under the same name.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts or correlated instruments.",
              "Metals futures (GC, SI, MGC, HG, PL, PA) suspended as of March 2026 — no return date announced.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "100% of approved payout amount.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "5 qualifying trading days required per payout cycle.",
              "50% consistency rule must be satisfied to request payout.",
              "Balance must remain above Safety Net after withdrawal.",
              "Trading may continue after submitting a payout request.",
            ]},
            { bold: "Limits", lines: [
              "6-payout ladder per PA — cap increases with each successful withdrawal. After 6 payouts, the PA closes.",
              "Payout caps by step — 25K: $1,000 (all 6). 50K: $1,500 / $2,000 / $2,500 / $2,500 / $3,000 / $3,000. 100K: $2,000 / $2,500 / $3,000 / $3,000 / $4,000 / $4,000. 150K: $2,500 / $3,000 / $3,000 / $4,000 / $4,000 / $5,000.",
              "Minimum payout: $500.",
            ]},
            { bold: "Processing", lines: [
              "Processed via Deel — automated, no manual review.",
              "Typically 24–48 hours review; funds arrive in 3–7 business days.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below the intraday trailing threshold — immediate liquidation and account failure.",
              "Dropping below the Safety Net after submitting a payout request — payout denied automatically.",
            ]},
            { bold: "Inactivity", lines: [
              "Failing to activate the PA within 7 calendar days after passing the eval — eval is forfeited.",
              "Must have 2 trading days with $50+ net profit within every rolling 30-day period or PA may close.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using the trailing threshold as a stop-loss mechanism.",
              "Hedging across accounts or correlated instruments.",
              "Fully automated or hands-off trading.",
              "Repeated or blatant news trading violations on the PA.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "alpha", name: "Alpha Futures", tagline: "London-backed. Multiple account types. Real flexibility.",
    bestFor: "Traders who want London-backed capital and multiple account types.",
    logo: "https://www.google.com/s2/favicons?domain=alpha-futures.com&sz=64",
    founded: "2024", location: "UK", regulated: false, website: "alpha-futures.com",
    drawdownType: "EOD Trailing", profitSplit: "70–90% (Std) / 90% (Adv/Zero)",
    newsTrading: true, overnightAllowed: true, hasDLL: true, maxFundedAccts: "3 qualified",
    maxPayout: "$15,000 (Advanced)", daysToPass: "2 days",
    platforms: ["Tradovate", "TradingView", "DeepChart", "Quantower", "AlphaTrader (coming soon)"], trustpilotRating: 4.9,
    products: [
      {
        name: "Standard", badge: "70→80→90% scaling · DLG on qualified",
        params: [["Profit Target","$3K / $6K / $9K"],["Max Loss Limit","4% EOD Trailing"],["Daily Loss Limit","DLG (soft) — qualified only"],["Consistency Rule","50% eval / 40% qualified"],["Max Position Size","5 / 10 / 15 minis by size"],["Min Trading Days","2 eval / 5 winning days qualified"]],
        sizes: ["50K", "100K", "150K"],
        pricing: [
          { label: "Eval price (sale)", values: ["$67.15", "$135.15", "$203.15"] },
          { label: "Regular price", values: ["$79", "$159", "$239"] },
          { label: "Activation fee", values: ["$149", "$149", "$149"] },
          { label: "Reset fee", values: ["$59", "$129", "$199"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (4%)", values: ["$2,000", "$4,000", "$6,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["50%", "50%", "50%"], color: null },
            { label: "Min trading days", values: ["2", "2", "2"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Qualified Account",
          rows: [
            { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$2,000", "$4,000", "$6,000"], color: C.red },
            { label: "DLG", values: ["$1,000", "$2,000", "$3,000"], color: null },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["40%", "40%", "40%"], color: null },
            { label: "Qualifying days", values: ["5", "5", "5"], color: null },
            { label: "Max payout", values: ["$15,000", "$15,000", "$15,000"], color: C.green },
            { label: "Profit split", values: ["70→90%", "70→90%", "70→90%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing — trails highest end-of-day balance, NOT intraday equity.",
              "MLL triggered by both unrealized AND realized P&L — open losing positions count in real time.",
              "MLL stops trailing once it reaches starting balance — locks permanently.",
              "Withdrawals reduce account balance — withdrawing to MLL level closes account.",
            ]},
            { bold: "Daily Loss Guard (DLG)", lines: [
              "Soft limit — locks trading for the day, does NOT fail the account.",
              "Calculated on daily P&L including unrealized + realized + simulated commissions.",
              "DLG applies on qualified accounts only — not during evaluation.",
            ]},
            { bold: "Consistency Rule", lines: [
              "Evaluation: 50% — best day must be under 50% of total profit. Must satisfy to pass.",
              "Qualified: 40% — resets after each payout request.",
              "Violating consistency does NOT breach the account — just blocks payout until satisfied.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 3 qualified accounts simultaneously.",
              "Monthly subscription — rebills every 30 days until pass or cancel.",
            ]},
            { bold: "Alpha Prime", lines: [
              "Triggered by $40K sim profit OR 5 payouts per qualified account.",
              "Account reviewed for transition to live capital.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "Qualified only: no new orders within 2 min before OR after high-impact events.",
              "High impact = red folder events on ForexFactory for the asset being traded.",
              "Can hold existing positions through news — restriction is on new orders only.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No fully automated trading — no bots, no AI, no hands-off systems.",
              "Semi-automated (signals/indicators with manual execution) is allowed.",
              "No tick/micro scalping — consistent pattern of trades under 2 min AND under 10 ticks.",
              "No \"All or Nothing\" — max leverage with DLG as only stop loss.",
            ]},
            { bold: "Account Conduct", lines: [
              "No hedging or reverse trading across accounts.",
              "No account stacking, rolling, or group trading.",
              "No VPN (VPS is allowed).",
            ]},
            { bold: "Market Rules", lines: [
              "No trading within 2% of CME daily price limit.",
              "No exploiting gapped or illiquid markets for isolated fills.",
              "Max 100 trades per day.",
              "No order book spamming.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "70% on payouts 1–2 → 80% on payouts 3–4 → 90% on payouts 5+.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout window: every 14 days from first trade on qualified account.",
              "5 qualifying days required per payout cycle.",
              "40% consistency rule must be met to request.",
            ]},
            { bold: "Limits", lines: [
              "Min payout: $200.",
              "Max payout: $15,000 per request.",
              "Withdrawals reduce account balance — plan accordingly.",
            ]},
            { bold: "Processing", lines: [
              "48 business hours or less.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — immediate liquidation.",
              "Withdrawing balance down to MLL level — account closes permanently.",
            ]},
            { bold: "Inactivity", lines: [
              "Must place at least 1 trade every 10 trading days or account archived.",
            ]},
            { bold: "Rule Violations", lines: [
              "Repeated news trading violations on qualified (first = warning, then breach).",
              "Using bots, AI, or fully automated trading.",
              "Hedging or reverse trading across accounts.",
              "Account stacking, rolling, or group trading.",
            ]},
          ]},
        ],
      },
      {
        name: "Advanced", badge: "90% flat · No DLG · No news ban",
        params: [["Profit Target","$4K / $8K / $12K"],["Max Loss Limit","3.5% EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","50% eval / None qualified"],["Max Position Size","5 / 10 / 15 minis by size"],["Min Trading Days","2 eval / 5 winning days qualified"]],
        sizes: ["50K", "100K", "150K"],
        pricing: [
          { label: "Eval price (sale)", values: ["$118.15", "$237.15", "$356.15"] },
          { label: "Regular price", values: ["$139", "$279", "$419"] },
          { label: "Activation fee", values: ["$149", "$149", "$149"] },
          { label: "Reset fee", values: ["$139", "$279", "$419"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$4,000", "$8,000", "$12,000"], color: C.green },
            { label: "Max drawdown (3.5%)", values: ["$1,750", "$3,500", "$5,250"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["50%", "50%", "50%"], color: null },
            { label: "Min trading days", values: ["2", "2", "2"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Qualified Account",
          rows: [
            { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$1,750", "$3,500", "$5,250"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
            { label: "Qualifying days", values: ["5", "5", "5"], color: null },
            { label: "Max payout", values: ["$15,000", "$15,000", "$15,000"], color: C.green },
            { label: "Profit split", values: ["90%", "90%", "90%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing — trails highest end-of-day balance, NOT intraday equity.",
              "MLL triggered by both unrealized AND realized P&L — open losing positions count in real time.",
              "MLL stops trailing once it reaches starting balance — locks permanently.",
              "Withdrawals reduce account balance — withdrawing to MLL level closes account.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 3 qualified accounts simultaneously.",
              "Monthly subscription — rebills every 30 days until pass or cancel.",
            ]},
            { bold: "Alpha Prime", lines: [
              "Triggered by $40K sim profit OR 5 payouts per qualified account.",
              "Account reviewed for transition to live capital.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "No restrictions — trade freely through any economic event on eval and qualified.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No fully automated trading — no bots, no AI, no hands-off systems.",
              "Semi-automated (signals/indicators with manual execution) is allowed.",
              "No tick/micro scalping — consistent pattern of trades under 2 min AND under 10 ticks.",
              "No \"All or Nothing\" — max leverage with MLL as only stop loss.",
            ]},
            { bold: "Account Conduct", lines: [
              "No hedging or reverse trading across accounts.",
              "No account stacking, rolling, or group trading.",
              "No VPN (VPS is allowed).",
            ]},
            { bold: "Market Rules", lines: [
              "No trading within 2% of CME daily price limit.",
              "No exploiting gapped or illiquid markets for isolated fills.",
              "Max 100 trades per day.",
              "No order book spamming.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90% flat — no scaling tiers.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout window: every 14 days from first trade on qualified account.",
              "5 qualifying days required per payout cycle.",
              "No consistency rule required for payout.",
            ]},
            { bold: "Limits", lines: [
              "Min payout: $1,000.",
              "Max payout: $15,000 per request.",
              "Up to 50% of profit per request until 30 winning days ($200+ each), then up to 100%.",
              "Withdrawals reduce account balance — plan accordingly.",
            ]},
            { bold: "Processing", lines: [
              "48 business hours or less.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — immediate liquidation.",
              "Withdrawing balance down to MLL level — account closes permanently.",
            ]},
            { bold: "Inactivity", lines: [
              "Must place at least 1 trade every 10 trading days or account archived.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using bots, AI, or fully automated trading.",
              "Hedging or reverse trading across accounts.",
              "Account stacking, rolling, or group trading.",
            ]},
          ]},
        ],
      },
      {
        name: "Zero", badge: "$0 activation · 90% flat · DLG on all phases",
        params: [["Profit Target","$1.5K / $3K / $6K"],["Max Loss Limit","4% EOD Trailing"],["Daily Loss Limit","DLG (soft) — eval + qualified"],["Consistency Rule","None eval / 40% qualified"],["Max Position Size","1 / 3 / 6 minis by size"],["Min Trading Days","None eval / 5 winning days qualified"]],
        sizes: ["25K", "50K", "100K"],
        pricing: [
          { label: "Eval price (sale)", values: ["$67.15", "$101.15", "$203.15"] },
          { label: "Regular price", values: ["$79", "$119", "$239"] },
          { label: "Activation fee", values: ["None", "None", "None"], color: C.textDim },
          { label: "Reset fee", values: ["$69", "$109", "$219"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000", "$6,000"], color: C.green },
            { label: "Max drawdown (4%)", values: ["$1,000", "$2,000", "$3,000"], color: C.red },
            { label: "DLG", values: ["$500", "$1,000", "$2,000"], color: null },
            { label: "Max contracts", values: ["1", "3", "6"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None"], color: C.textDim },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Qualified Account",
          rows: [
            { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000"], color: C.red },
            { label: "DLG", values: ["$500", "$1,000", "$2,000"], color: null },
            { label: "Max contracts", values: ["1", "3", "6"], color: null },
            { label: "Consistency rule", values: ["40%", "40%", "40%"], color: null },
            { label: "Qualifying days", values: ["5", "5", "5"], color: null },
            { label: "Max payout", values: ["$1,000", "$1,500", "$2,500"], color: C.green },
            { label: "Profit split", values: ["90%", "90%", "90%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing — trails highest end-of-day balance, NOT intraday equity.",
              "MLL triggered by both unrealized AND realized P&L — open losing positions count in real time.",
              "MLL stops trailing once it reaches starting balance — locks permanently.",
              "Withdrawals reduce account balance — withdrawing to MLL level closes account.",
            ]},
            { bold: "Daily Loss Guard (DLG)", lines: [
              "Soft limit — locks trading for the day, does NOT fail the account.",
              "Calculated on daily P&L including unrealized + realized + simulated commissions.",
              "DLG applies on BOTH evaluation and qualified accounts — all phases.",
            ]},
            { bold: "Consistency Rule", lines: [
              "Evaluation: none — can pass in a single day.",
              "Qualified: 40% — best day since last payout must be under 40% of total net profit.",
              "Resets after each payout request.",
              "Violating consistency does NOT breach the account — just blocks payout until satisfied.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 3 qualified accounts simultaneously.",
              "Monthly subscription — rebills every 30 days until pass or cancel.",
            ]},
            { bold: "Unique Features", lines: [
              "$0 activation fee — no upfront cost after passing evaluation.",
              "Scaling rule on $50K and $100K qualified: contract limits start lower and increase over time.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "No restrictions — trade freely through any economic event on eval and qualified.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No fully automated trading — no bots, no AI, no hands-off systems.",
              "Semi-automated (signals/indicators with manual execution) is allowed.",
              "No tick/micro scalping — consistent pattern of trades under 2 min AND under 10 ticks.",
              "No \"All or Nothing\" — max leverage with MLL as only stop loss.",
            ]},
            { bold: "Account Conduct", lines: [
              "No hedging or reverse trading across accounts.",
              "No account stacking, rolling, or group trading.",
              "No VPN (VPS is allowed).",
            ]},
            { bold: "Market Rules", lines: [
              "No trading within 2% of CME daily price limit.",
              "No exploiting gapped or illiquid markets for isolated fills.",
              "Max 100 trades per day.",
              "No order book spamming.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90% flat — no scaling tiers.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "5 qualifying days required per payout cycle.",
              "40% consistency rule must be met to request payout.",
            ]},
            { bold: "Limits", lines: [
              "Min payout: $200.",
              "Max payout: $1,000 (25K) / $1,500 (50K) / $2,500 (100K).",
              "Up to 50% of profit per request until 30 winning days ($200+ each), then up to 100%.",
              "Withdrawals reduce account balance — plan accordingly.",
            ]},
            { bold: "Processing", lines: [
              "48 business hours or less.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — immediate liquidation.",
              "Withdrawing balance down to MLL level — account closes permanently.",
            ]},
            { bold: "Inactivity", lines: [
              "Must place at least 1 trade every 10 trading days or account archived.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using bots, AI, or fully automated trading.",
              "Hedging or reverse trading across accounts.",
              "Account stacking, rolling, or group trading.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "tradeify", name: "Tradeify", tagline: "Choose your funded path. Daily or Flex. One eval, you decide after.",
    bestFor: "Tradovate users who want path flexibility and a route to live capital via Elite.",
    logo: "https://www.google.com/s2/favicons?domain=tradeify.co&sz=64",
    founded: "2023", location: "USA", regulated: false, website: "tradeify.co",
    drawdownType: "EOD Trailing", profitSplit: "90%",
    newsTrading: true, overnightAllowed: false, hasDLL: true, maxFundedAccts: "5 funded",
    maxPayout: "$25,000 (Growth)", daysToPass: "1 day",
    platforms: ["Tradovate", "NinjaTrader (via Tradovate)", "TradingView", "Quantower", "WealthCharts", "Rithmic", "TradeSea (via Rithmic)"], trustpilotRating: 4.6,
    products: [
      {
        name: "Select", badge: "1-step eval · Flex or Daily path · No funded consistency",
        params: [["Profit Target","$1.5K / $3K / $6K / $9K"],["Max Drawdown","$1K / $2K / $3K / $4.5K EOD Trailing"],["Daily Loss Limit","None eval / path-dependent funded"],["Consistency Rule","40% eval / None funded"],["Max Position Size","1 / 4 / 8 / 12 by size"],["Min Trading Days","3 eval / 5 winning days funded"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "Price (discounted)", values: ["$65", "$99", "$159", "$221"] },
          { label: "Regular price", values: ["$109", "$165", "$265", "$369"] },
          { label: "Activation fee", values: ["None", "None", "None", "None"], color: C.textDim },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (EOD)", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["1", "4", "8", "12"], color: null },
            { label: "Consistency rule", values: ["40%", "40%", "40%", "40%"], color: null },
            { label: "Min trading days", values: ["3", "3", "3", "3"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None", "None", "None", "None"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Funded Account",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (Flex)", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
            { label: "Daily loss limit (Flex)", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Starting contracts", values: ["1", "2", "3", "3"], color: null },
            { label: "Max contracts", values: ["1", "4", "8", "12"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Winning days", values: ["5", "5", "5", "5"], color: null },
            { label: "Max payout", values: ["$1,250", "$3,000", "$4,000", "$5,000"], color: C.green },
            { label: "Profit split", values: ["90%", "90%", "90%", "90%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — recalculates once per day at 5:00 PM ET based on highest end-of-day balance.",
              "Drawdown locks at starting balance + $100 once EOD balance exceeds drawdown amount by $100.",
              "On Select Flex, drawdown also locks permanently on first approved payout — whichever comes first.",
              "Once locked, drawdown becomes a static floor and never moves again.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "No DLL on the Flex path.",
              "Select Daily has a DLL (soft) — acts as a session stop, pauses trading for the day but does NOT fail the account.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Funded accounts start at reduced contracts and scale up to full size as account balance grows.",
            ]},
            { bold: "Account Limits", lines: [
              "One-time purchase — no recurring billing. No activation fee; no monthly fee once funded.",
              "Failed evals can be reset with a reset fee. Failed funded accounts cannot be reset — must pass a new evaluation.",
            ]},
            { bold: "Unique Features", lines: [
              "1-step evaluation, then permanently choose funded payout path (Flex or Daily) per account.",
              "Tradeify Elite: 5 total payouts across any Tradeify accounts qualifies for live CME capital (90/10 split, daily payouts).",
              "Tradeify Elite Live Performance Reward Pool: each live account gets a reward pool ($2K/25K, $4K/50K, $8K/100K, $12K/150K). Select traders can earn 1.5x multiplier for disciplined sim-funded trading.",
              "Instant activation — funded accounts can be activated immediately from the dashboard upon passing.",
              "Microscalping rule: 50%+ of trades AND 50%+ of profit must be held >10 seconds — checked at eval activation and payout requests.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading allowed — no time buffers around economic events.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No HFT bots. Personal algos/bots allowed with proof of ownership and verification.",
              "No exploiting platform or pricing errors, latency arbitrage, or spoofing.",
            ]},
            { bold: "Position Rules", lines: [
              "No hedging — no opposite positions on the same instrument, even across accounts.",
              "No trading minis and micros at the same time (10-second grace period to close conflicts).",
            ]},
            { bold: "Market Rules", lines: [
              "Overnight not allowed — all positions must be flat by 4:59 PM ET or auto-closed.",
              "Must place at least 1 trade per week (Monday–Friday) to remain active.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10 from payout 1.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "FLEX: Payout available after every 5 winning days. No minimum balance for first payout.",
              "Winning day minimum: $100 (25K) / $150 (50K) / $200 (100K) / $250 (150K).",
              "Flex 2nd+ payouts require positive net profit during the current cycle.",
              "DAILY: Payout available daily once funded. A buffer must be maintained — cannot withdraw below buffer amount.",
              "Buffer required above starting balance: $1,100 (25K) / $2,100 (50K) / $2,600 (100K) / $3,600 (150K).",
              "Daily minimum payout: $250.",
            ]},
            { bold: "Limits", lines: [
              "Flex payout: up to 50% of total profits, capped at $1,250 (25K) / $3,000 (50K) / $4,000 (100K) / $5,000 (150K) per cycle.",
              "Daily payout: up to 2x net profit since last payout, capped at $600 (25K) / $1,000 (50K) / $1,500 (100K) / $2,500 (150K) per day. Min payout $250.",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Rise (primary — ACH, instant bank, crypto, 7 days/week) | Plane (international).",
              "Typically 24–48 hours; up to 72 hours outside business hours.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Breaching trailing drawdown fails the account — hard breach, no recovery.",
              "Failed funded accounts cannot be reset — must pass a new evaluation.",
            ]},
            { bold: "Inactivity", lines: [
              "Missing 1 trade per week (Monday–Friday) can lead to account deactivation.",
            ]},
            { bold: "Rule Violations", lines: [
              "Hedging or holding minis and micros simultaneously can lead to account termination.",
              "Using HFT or prohibited exploit behavior can lead to termination.",
              "Violating microscalping rule blocks eval activation or payout — does not auto-fail.",
              "Select Daily: hitting DLL stops trading for the session — not a full account fail.",
            ]},
          ]},
        ],
      },
      {
        name: "Growth", badge: "Pass in 1 day · DLL · 35% funded consistency",
        params: [["Profit Target","$1,500 / $3,000 / $6,000 / $9,000"],["Max Drawdown","$1K / $2K / $3.5K / $5K EOD Trailing"],["Daily Loss Limit","$600 / $1,250 / $2,500 / $3,750"],["Consistency Rule","None eval / 35% funded"],["Max Position Size","1 / 4 / 8 / 12 by size"],["Min Trading Days","None eval / 5 profitable days funded"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "Price (discounted)", values: ["$59", "$87", "$153", "$221"] },
          { label: "Regular price", values: ["$99", "$145", "$255", "$369"] },
          { label: "Activation fee", values: ["None", "None", "None", "None"], color: C.textDim },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (EOD)", values: ["$1,000", "$2,000", "$3,500", "$5,000"], color: C.red },
            { label: "Daily loss limit", values: ["$600", "$1,250", "$2,500", "$3,750"], color: null },
            { label: "Max contracts", values: ["1", "4", "8", "12"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None", "None", "None", "None"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Funded Account",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,500", "$5,000"], color: C.red },
            { label: "Daily loss limit", values: ["$600", "$1,250", "$2,500", "$3,750"], color: null },
            { label: "Max contracts", values: ["1", "4", "8", "12"], color: null },
            { label: "Consistency rule", values: ["35%", "35%", "35%", "35%"], color: null },
            { label: "Profitable days", values: ["5", "5", "5", "5"], color: null },
            { label: "Max payout", values: ["$1,000 (tiered)", "$3,000 (tiered)", "$4,000 (tiered)", "$5,000 (tiered)"], color: C.green },
            { label: "Profit split", values: ["90%", "90%", "90%", "90%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — recalculates at 5:00 PM ET session close based on highest end-of-day balance.",
              "Drawdown locks at starting balance + $100 once trigger is reached.",
              "Once locked, floor is static and never moves again.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "DLL applies to both eval and funded — acts as a session stop, pauses trading for the day but does NOT fail the account.",
              "Once balance reaches 6% above starting (e.g. $53K on 50K), DLL automatically increases to match the full drawdown amount.",
            ]},
            { bold: "Consistency Rule", lines: [
              "35% Consistency Rule on funded: no single day can represent more than 35% of total net profit.",
              "Profitable-day count resets to zero after each approved payout.",
              "Microscalping rule: 50%+ of trades AND 50%+ of profit must be held >10 seconds.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 5 funded accounts across all Tradeify types combined.",
              "One-time purchase — no recurring billing. No activation fee; no monthly fee once funded.",
              "Failed evals can be reset with a reset fee. Failed funded accounts cannot be reset — must pass a new evaluation.",
            ]},
            { bold: "Unique Features", lines: [
              "Tradeify Elite: 5 total payouts across any Tradeify accounts qualifies for live CME capital.",
              "Tradeify Elite Live Performance Reward Pool: each live account gets a reward pool ($2K/25K, $4K/50K, $8K/100K, $12K/150K).",
              "Instant activation — funded accounts can be activated immediately from the dashboard upon passing.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading allowed.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No HFT bots. Personal algos/bots allowed with proof of ownership.",
              "No exploiting platform or pricing errors.",
            ]},
            { bold: "Position Rules", lines: [
              "No hedging — no opposite positions on the same instrument.",
              "No trading minis and micros at the same time.",
            ]},
            { bold: "Market Rules", lines: [
              "Overnight not allowed — positions must be flat by 4:59 PM ET or auto-closed.",
              "Must place at least 1 trade per week to remain active.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "5 profitable days required for each payout cycle — count resets after each approved payout.",
              "Minimum account balance required before requesting payout: $1,500 (25K) / varies by size.",
            ]},
            { bold: "Limits", lines: [
              "Payout caps increase per cycle: capped at $1,000 (25K) / $3,000 (50K) / $4,000 (100K) / $5,000 (150K) for early payouts, scaling up to $25,000 from payout 6+. Min payout: $500.",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Rise (primary) | Plane (international).",
              "Typically 24–48 hours; up to 72 hours outside business hours.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Breaching trailing drawdown fails the account — hard breach, no recovery.",
              "Failed funded accounts cannot be reset — must pass a new evaluation.",
            ]},
            { bold: "Inactivity", lines: [
              "Missing 1 trade per week can lead to account deactivation.",
            ]},
            { bold: "Rule Violations", lines: [
              "Hitting DLL only stops trading for the session — not a fail.",
              "Missing 35% consistency blocks payout — does not itself fail the account.",
              "Hedging can lead to termination.",
              "Using prohibited exploit behavior can lead to termination.",
              "Violating microscalping rule can block payout.",
            ]},
          ]},
        ],
      },
      {
        name: "Lightning", badge: "Instant funded · No eval · Progressive consistency",
        params: [["Profit Target","None"],["Max Drawdown","$1K / $2K / $4K / $5.25K EOD Trailing"],["Daily Loss Limit","None (25K) / $1,250 / $2,500 / $3,000"],["Consistency Rule","20% → 25% → 30% progressive"],["Max Position Size","1 / 4 / 8 / 12 by size"],["Min Trading Days","None"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "Price (discounted)", values: ["$207", "$295", "$396", "$478"] },
          { label: "Regular price", values: ["$345", "$492", "$660", "$796"] },
        ],
        evalTable: null,
        fundedTable: {
          title: "Funded Account",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$1,000", "$2,000", "$4,000", "$5,250"], color: C.red },
            { label: "Daily loss limit", values: ["None", "$1,250", "$2,500", "$3,000"], color: null },
            { label: "Max contracts", values: ["1", "4", "8", "12"], color: null },
            { label: "Consistency rule", values: ["20→30%", "20→30%", "20→30%", "20→30%"], color: null },
            { label: "Min trading days", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max payout", values: ["$1,000", "$3,000 (tiered)", "$4,000 (tiered)", "$5,000 (tiered)"], color: C.green },
            { label: "Profit split", values: ["90%", "90%", "90%", "90%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — recalculates at 5:00 PM ET session close based on highest end-of-day balance.",
              "Drawdown locks at starting balance + $100 once trigger is reached.",
              "Once locked, floor is static and never moves again.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "25K has no DLL — trailing drawdown is the only risk control.",
              "50K/100K/150K have a DLL (soft stop) — pauses trading for the session but does NOT fail the account.",
            ]},
            { bold: "Consistency Rule", lines: [
              "Progressive consistency: 20% on 1st payout → 25% on 2nd → 30% from 3rd onwards.",
              "No single day can exceed the applicable percentage of total net profit.",
              "No minimum trading days for payout — just meet the profit goal and consistency threshold.",
              "Payout profit goals reset after each approved payout.",
              "Microscalping rule: 50%+ of trades AND 50%+ of profit must be held >10 seconds.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 5 funded accounts across all Tradeify types combined.",
              "One-time purchase — no recurring billing. Cannot be reset if failed.",
            ]},
            { bold: "Unique Features", lines: [
              "Instant funded account — no evaluation required.",
              "Tradeify Elite: 5 total payouts across any Tradeify accounts qualifies for live CME capital.",
              "Tradeify Elite Live Performance Reward Pool: each live account gets a reward pool ($2K/25K, $4K/50K, $8K/100K, $12K/150K).",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading allowed.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No HFT bots. Personal algos/bots allowed with proof of ownership.",
              "No exploiting platform or pricing errors.",
            ]},
            { bold: "Position Rules", lines: [
              "No hedging — no opposite positions on the same instrument.",
              "No trading minis and micros at the same time.",
            ]},
            { bold: "Market Rules", lines: [
              "Overnight not allowed — positions must be flat by 4:59 PM ET or auto-closed.",
              "Must place at least 1 trade per week to remain active.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Daily payout eligibility — no minimum trading days required.",
              "Must meet the payout profit goal for the current cycle before requesting.",
              "Minimum payout: $1,000.",
            ]},
            { bold: "Limits", lines: [
              "Payout caps match Growth: $3,000 (50K) / $4,000 (100K) / $5,000 (150K) for early payouts, scaling up to $25,000 from payout 6+. 25K: fixed cap of $1,000 per payout.",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Rise (primary) | Plane (international).",
              "Typically within 24 hours; up to 72 hours outside business hours.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Breaching trailing drawdown fails the account — hard breach, no recovery.",
              "Lightning accounts cannot be reset if failed — must purchase a new one.",
            ]},
            { bold: "Inactivity", lines: [
              "Missing 1 trade per week can lead to account deactivation.",
            ]},
            { bold: "Rule Violations", lines: [
              "On 50K/100K/150K, hitting DLL only stops trading for the session — not a fail.",
              "Failing consistency or payout profit goals blocks payout — does not itself fail the account.",
              "Hedging can lead to termination.",
              "Using prohibited exploit behavior can lead to termination.",
              "Violating microscalping rule can block payout.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "mffu", name: "MyFundedFutures", tagline: "Four distinct paths. EOD or intraday. Real live account trigger.",
    bestFor: "Traders who want Flex, Pro, Rapid, or Builder paths with clear live triggers.",
    logo: "https://www.google.com/s2/favicons?domain=myfundedfutures.com&sz=64",
    founded: "2023", location: "USA", regulated: false, website: "myfundedfutures.com",
    drawdownType: "EOD (Flex/Pro) / Intraday (Rapid)", profitSplit: "80% (Flex/Pro) / 90% (Rapid)",
    newsTrading: true, overnightAllowed: false, hasDLL: false,
    maxFundedAccts: "5 funded per household",
    maxPayout: "No cap (Pro)", daysToPass: "2 days",
    platforms: ["Tradovate", "NinjaTrader", "Quantower", "Volumetrica", "TradingView", "Volsys", "Volbook", "ATAS Orderflow Trading"], trustpilotRating: 4.9,
    products: [
      {
        name: "Flex", badge: "EOD · 80% · News allowed funded · Replaces Core",
        params: [["Profit Target","$1.5K ($25K) | $3K ($50K)"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","50% eval / None funded"],["Max Position Size","3 / 5 minis by size"],["Min Trading Days","2 eval / 5 winning days funded"]],
        sizes: ["25K", "50K"],
        pricing: [
          { label: "Sale price", values: ["~$49", "$107"] },
          { label: "Regular price", values: ["$84", "$107"] },
          { label: "Activation fee", values: ["None", "None"], color: C.textDim },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000"], color: C.green },
            { label: "Max drawdown", values: ["$1,000", "$2,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["3", "5"], color: null },
            { label: "Consistency rule", values: ["50%", "50%"], color: null },
            { label: "Min trading days", values: ["2", "2"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None", "None"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Sim Funded",
          rows: [
            { label: "Profit target", values: ["None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$1,000", "$2,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["3", "5"], color: null },
            { label: "Consistency rule", values: ["None", "None"], color: C.textDim },
            { label: "Winning days", values: ["5", "5"], color: null },
            { label: "Max payout", values: ["$3,000", "$5,000"], color: C.green },
            { label: "Profit split", values: ["80%", "80%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing — MLL trails highest end-of-day balance, never intraday dips.",
              "After FIRST payout: MLL locks permanently at starting balance + $100 — no longer trails.",
              "Once locked, account breaches only if EOD balance drops below that floor.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Micro leverage: 1:1 (1 micro per 1 mini equivalent — NOT 1:10).",
              "Funded scaling by profit level — $25K: $0–749 = 1M | $750–999 = 2M | $1K+ = 3M.",
              "$50K: $0–1499 = 2M | $1500–1999 = 3M | $2K+ = 5M.",
              "No scaling rules during evaluation — start at max allowed contracts.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 10 active evaluations at once.",
              "Max 5 sim funded accounts per household (25K/50K combo only). If any account is 100K or 150K, max drops to 3 total. Max 3 Flex 50K accounts.",
              "Max total sim funded payout: $100,000 per user.",
            ]},
            { bold: "Unique Features", lines: [
              "T1 news trading ALLOWED on both evaluation and Flex funded accounts.",
              "Live transition: 5 sim funded payouts on same account, OR $100K sim profit cap reached.",
              "Live Flex: $1K balance ($25K) or $2K balance ($50K), EOD drawdown, no DLL. 21-day cooldown if live account fails.",
              "Net profit required between each payout: $250 ($25K) | $500 ($50K).",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading fully ALLOWED on both evaluation and funded — no blocked events.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Automated strategies allowed only if not exploiting simulator fills.",
              "Copy trading allowed with own specific settings — no exploiting sim environment.",
              "No straddle/strangle strategies or news burst exploitation.",
            ]},
            { bold: "Position Rules", lines: [
              "Positions auto-close at 4:10 PM EST — does NOT breach account.",
              "No overnight or weekend positions.",
            ]},
            { bold: "Market Rules", lines: [
              "No trading within 2% of CME daily price limit.",
              "Cross-instrument trading that exceeds total contract limit is prohibited.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "5 winning days required per payout cycle — $100/day min ($25K) | $150/day min ($50K).",
              "Net profit required between payouts: $250 ($25K) | $500 ($50K).",
            ]},
            { bold: "Limits", lines: [
              "Max payout: 50% of profits, up to $3,000 (25K) / $5,000 (50K) per payout.",
              "Min payout: $250.",
              "Remaining 50% of profits must stay in account.",
            ]},
            { bold: "Processing", lines: [
              "Most approved instantly, manual review up to 6–12 business hours.",
              "Payout method: Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance dropping below MLL (locks at starting balance + $100 after first payout).",
            ]},
            { bold: "Inactivity", lines: [
              "7-day inactivity — must place at least 1 trade every 7 calendar days or notify support.",
            ]},
            { bold: "Rule Violations", lines: [
              "Exceeding max contract size for account.",
              "Deliberately bypassing position scaling rules.",
              "Exploiting simulator fills with automated tools.",
              "No resets for sim funded accounts — must pass a new evaluation.",
            ]},
          ]},
        ],
      },
      {
        name: "Pro", badge: "EOD · 80% · No cap per payout · Bi-weekly",
        params: [["Profit Target","$3K ($50K) | $6K ($100K) | $9K ($150K)"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","50% eval / None funded"],["Max Position Size","5 / 10 / 15 minis by size"],["Min Trading Days","2 eval / None funded"]],
        sizes: ["50K", "100K", "150K"],
        pricing: [
          { label: "Sale price", values: ["~$114", "~$172", "~$239"] },
          { label: "Regular price", values: ["$227", "$344", "$477"] },
          { label: "Activation fee", values: ["None", "None", "None"], color: C.textDim },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown", values: ["$2,000", "$3,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["50%", "50%", "50%"], color: null },
            { label: "Min trading days", values: ["2", "2", "2"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None", "None", "None"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Sim Funded",
          rows: [
            { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$2,000", "$3,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
            { label: "News trading", values: ["Blocked", "Blocked", "Blocked"], color: C.red },
            { label: "Payout buffer", values: ["$2,100", "$3,100", "$4,600"], color: null },
            { label: "Max payout", values: ["No cap", "No cap", "No cap"], color: C.green },
            { label: "Profit split", values: ["80%", "80%", "80%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing — MLL trails highest end-of-day balance, never intraday dips.",
              "After first payout: MLL locks permanently at starting balance + $100 — no longer trails.",
              "No Daily Loss Limit.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 10 active evaluations at once.",
              "Max 5 sim funded accounts per household (25K/50K combo only). If any account is 100K or 150K, max drops to 3 total.",
              "Max total sim funded payout: $100,000 per user (across all Pro accounts combined).",
            ]},
            { bold: "Unique Features", lines: [
              "1-Day Add-On available: $4,000 profit target on $50K with NO consistency rule — can pass in one day.",
              "Micro leverage: 1:1 (1 micro per 1 mini — NOT 1:10).",
              "No scaling requirements on funded — full position sizes from day one.",
              "Live transition: 3 consecutive payouts OR $20,000 profit milestone. Risk team may fast-track.",
              "Excess profits beyond $100K sim cap may transfer to live (up to plan-specific limits).",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "T1 news BLOCKED on funded — no open positions or orders 2 min before AND 2 min after T1 releases.",
              "T1 events: FOMC, FOMC Minutes, Employment Report, CPI, EIA (Wednesdays 10:30 AM), Agricultural Reports.",
              "News trading allowed during evaluation.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Automated strategies allowed only if not exploiting simulator fills.",
              "No straddle/strangle strategies or news burst exploitation.",
            ]},
            { bold: "Position Rules", lines: [
              "Positions auto-close at 4:10 PM EST — does NOT breach account.",
              "No overnight or weekend positions.",
            ]},
            { bold: "Market Rules", lines: [
              "No trading within 2% of CME daily price limit.",
              "Cross-instrument trading that exceeds total contract limit is prohibited.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout frequency: every 14 calendar days from first trade.",
              "Early withdrawal before buffer cleared: up to 60% of profits, min $1,000.",
            ]},
            { bold: "Limits", lines: [
              "Buffer: $2,100 ($50K) | $3,100 ($100K) | $4,600 ($150K).",
              "Min payout: $1,000. No max payout per request.",
              "Max total sim funded payout: $100,000 per user.",
            ]},
            { bold: "Processing", lines: [
              "Most approved instantly, manual review up to 6–12 business hours.",
              "Payout method: Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance dropping below MLL (locks at starting balance + $100 after first payout).",
            ]},
            { bold: "Inactivity", lines: [
              "7-day inactivity — must place at least 1 trade every 7 calendar days or notify support.",
            ]},
            { bold: "Rule Violations", lines: [
              "Trading during T1 news events on Pro funded.",
              "Exceeding max contract size.",
              "Deliberately bypassing position scaling rules.",
              "No resets for sim funded accounts.",
            ]},
          ]},
        ],
      },
      {
        name: "Rapid", badge: "INTRADAY trailing · 90% · Daily payouts · $10K/day cap",
        params: [["Profit Target","$1.5K ($25K) | $3K ($50K) | $6K ($100K) | $9K ($150K)"],["Max Loss Limit","INTRADAY Trailing (real-time HWM)"],["Daily Loss Limit","None"],["Consistency Rule","50% eval / None funded"],["Max Position Size","3 / 5 / 10 / 15 minis by size"],["Min Trading Days","2 eval / None funded"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "Sale price", values: ["~$87", "~$126", "~$214", "~$278"] },
          { label: "Regular price", values: ["$109", "$157", "$267", "$347"] },
          { label: "Activation fee", values: ["None", "None", "None", "None"], color: C.textDim },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (Intraday)", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["3", "5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["50%", "50%", "50%", "50%"], color: null },
            { label: "Min trading days", values: ["2", "2", "2", "2"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None", "None", "None", "None"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Sim Funded (Rapid)",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (Intraday)", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["3", "5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "News trading", values: ["Blocked", "Blocked", "Blocked", "Blocked"], color: C.red },
            { label: "Daily profit cap", values: ["$10,000", "$10,000", "$10,000", "$10,000"], color: null },
            { label: "Max payout", values: ["No cap", "No cap", "No cap", "No cap"], color: C.green },
            { label: "Profit split", values: ["90%", "90%", "90%", "90%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "INTRADAY trailing — trails highest equity HWM including unrealized P&L in real time.",
              "Every new equity peak permanently raises the drawdown floor, even on scratch trades.",
              "Once MLL reaches starting balance + $100, it locks permanently.",
              "Not recommended for beginners — intraday trailing is unforgiving.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Micro leverage: 1:10 (10 micros per 1 mini equivalent).",
              "Starting balance: $0 — build from scratch.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 10 active evaluations at once.",
              "Max 5 sim funded accounts per household (25K/50K combo only). If any account is 100K or 150K, max drops to 3 total.",
              "Max total sim funded payout: $100,000 per user.",
            ]},
            { bold: "Daily Profit Cap", lines: [
              "$10,000 daily profit cap on Rapid sim funded — excess forfeited.",
              "Hitting $10K in one day triggers automatic risk review for live transition.",
            ]},
            { bold: "Live Transition", lines: [
              "Auto-triggers if $10K profit in one day. Up to $5K moved to Reserve program.",
              "Rapid Live switches to EOD drawdown — easier than sim intraday. Fewer contracts than sim.",
              "Reserve program: up to $5K held as deposits for live account. Unlocked after 20 profitable days + $10K in live payouts.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "T1 news BLOCKED on funded — no positions or orders 2 min before AND 2 min after T1 releases.",
              "T1 events: FOMC, FOMC Minutes, Employment Report, CPI, EIA (Wednesdays 10:30 AM), Agricultural Reports.",
              "News trading allowed during evaluation.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Automated strategies allowed only if not exploiting simulator fills.",
              "No straddle/strangle strategies or news burst exploitation.",
            ]},
            { bold: "Position Rules", lines: [
              "Positions auto-close at 4:10 PM EST — does NOT breach account.",
              "No overnight or weekend positions.",
            ]},
            { bold: "Market Rules", lines: [
              "No trading within 2% of CME daily price limit.",
              "Cross-instrument trading that exceeds total contract limit is prohibited.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout frequency: DAILY — refreshes at market close.",
              "First payout eligible 24 hours after first trade (once buffer met).",
            ]},
            { bold: "Limits", lines: [
              "Buffer: $2,100 ($50K) | $3,100 ($100K) | $4,600 ($150K).",
              "Min payout: $500. No max payout per request.",
              "Daily profit cap: $10,000.",
              "Max total sim funded payout: $100,000 per user.",
            ]},
            { bold: "Processing", lines: [
              "Most approved instantly, manual review up to 6–12 business hours.",
              "Payout method: Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance dropping below MLL (locks at starting balance + $100 once triggered).",
              "Making more than $10,000 in one day — excess forfeited and triggers risk review.",
              "Holding large unrealized profits carelessly — open gains raise your floor in real time.",
            ]},
            { bold: "Inactivity", lines: [
              "7-day inactivity — must place at least 1 trade every 7 calendar days or notify support.",
            ]},
            { bold: "Rule Violations", lines: [
              "Trading during T1 news events on funded.",
              "Exceeding max contract size.",
              "Deliberately bypassing position scaling rules.",
              "No resets for sim funded accounts.",
            ]},
          ]},
        ],
      },
      {
        name: "Builder", badge: "EOD · 80% · 1-day pass · News allowed · $2K payout cap",
        params: [["Profit Target","$3K ($50K)"],["Max Loss Limit","EOD Trailing — $2K (Default) or $1.5K (Add-On)"],["Daily Loss Limit","$1,000 soft pause"],["Consistency Rule","None eval / 50% payout only"],["Max Position Size","4 Minis / 40 Micros"],["Min Trading Days","1 eval / 2 qualifying days payout"]],
        sizes: ["50K"],
        pricing: [
          { label: "Regular price", values: ["$153 (Default) / $125 (Add-On)"] },
          { label: "Sale price (30% off)", values: ["$107 (Default) / $87 (Add-On)"] },
          { label: "Activation fee", values: ["None"], color: C.textDim },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$3,000"], color: C.green },
            { label: "Max drawdown (EOD)", values: ["$2,000 (Default) / $1,500 (Add-On)"], color: C.red },
            { label: "Daily loss limit", values: ["$1,000 soft pause"], color: null },
            { label: "Max contracts", values: ["4 Minis / 40 Micros"], color: null },
            { label: "Consistency rule", values: ["None"], color: C.textDim },
            { label: "Min trading days", values: ["1"], color: null },
            { label: "News trading", values: ["Allowed"], color: C.green },
            { label: "Time limit", values: ["None"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Sim Funded (Builder)",
          rows: [
            { label: "Profit target", values: ["None"], color: null },
            { label: "Max drawdown (EOD)", values: ["$2,000 (Default) / $1,500 (Add-On)"], color: C.red },
            { label: "Daily loss limit", values: ["$1,000 soft pause"], color: null },
            { label: "Max contracts", values: ["4 Minis / 40 Micros"], color: null },
            { label: "Consistency rule", values: ["50% payout rule only"], color: null },
            { label: "Qualifying days/cycle", values: ["2"], color: null },
            { label: "Payout buffer", values: ["$2,100 (Default) / $1,600 (Add-On)"], color: null },
            { label: "Max payout", values: ["$2,000"], color: C.green },
            { label: "Profit split", values: ["80%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing MLL — only adjusts at end of session based on closing balance. Never moves intraday.",
              "Two MLL options at checkout: $2,000 (Default) or $1,500 (Add-On, cheaper). Same rules either way.",
              "MLL locks permanently once it reaches $100 above starting balance — no further trailing.",
              "Sim Funded account starts at $0 — balance goes negative before MLL trails up to breakeven. This is normal.",
            ]},
            { bold: "Account Limits", lines: [
              "Only 1 Builder Sim Funded account active per user at any time.",
              "After a breach, earliest a new Sim Funded account can be activated is the following trading day.",
              "5 approved sim payouts → eligible for promotion to live funded account.",
              "7-day inactivity on sim funded closes the account.",
            ]},
            { bold: "Live Account", lines: [
              "Live transition triggers after 5th approved sim payout.",
              "Live MLL matches the option selected at checkout ($2,000 or $1,500 EOD trailing).",
              "Live payout: 80/20 split, processed daily. No consistency rule on live.",
              "Max 1 live Builder account.",
              "Live breach triggers 21-day cooldown: all sim funded trading prohibited, no new eval purchases.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading fully ALLOWED — no blocked events, no windows, on both eval and funded.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Automated strategies allowed only if not exploiting simulator fills.",
              "No straddle/strangle strategies or news burst exploitation.",
            ]},
            { bold: "Position Rules", lines: [
              "Positions auto-close at 4:10 PM EST — does NOT breach account.",
              "No overnight or weekend positions.",
            ]},
            { bold: "Market Rules", lines: [
              "No trading within 2% of CME daily price limit.",
              "Cross-instrument trading that exceeds total contract limit is prohibited.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20 — trader keeps 80% of each approved payout.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout requests available once buffer is cleared ($2,100 Default / $1,600 Add-On).",
              "2 qualifying trading days required per payout cycle.",
              "50% consistency rule: largest single profit day must be <50% of total cycle profit. Resets after each payout.",
              "$500 net profit above buffer for first payout; $500 net since last payout for all subsequent requests.",
            ]},
            { bold: "Limits", lines: [
              "Max payout per cycle: $2,000. Min payout: $500.",
              "Max sim payouts: 5 total, then eligible for live promotion.",
            ]},
            { bold: "Processing", lines: [
              "Most approved instantly, manual review up to 6–12 business hours.",
              "Payout method: Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "EOD balance dropping below the MLL at session close.",
            ]},
            { bold: "Inactivity", lines: [
              "7 consecutive calendar days without a trade closes the sim funded account.",
            ]},
            { bold: "Rule Violations", lines: [
              "Exceeding 4 minis / 40 micros max contract size.",
              "Exploiting simulator fills with automated tools.",
              "No resets for sim funded accounts — must pass a new evaluation.",
            ]},
            { bold: "Live Cooldown", lines: [
              "Live account breach triggers 21-day cooldown: sim funded trading halted, no new eval purchases allowed.",
              "Cooldown lifts after 21 calendar days — all restrictions removed.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "lucid", name: "Lucid Trading", tagline: "Newest major firm. Most platforms. Daily live payouts.",
    bestFor: "Traders who want the most platforms and no funded consistency (Flex).",
    logo: "https://www.google.com/s2/favicons?domain=lucidtrading.com&sz=64",
    founded: "2025", location: "USA", regulated: false, website: "lucidtrading.com",
    drawdownType: "EOD Trailing", profitSplit: "90%",
    newsTrading: true, overnightAllowed: false, hasDLL: true, maxFundedAccts: "5 funded",
    maxPayout: "$3,000 (LucidFlex 150K)", daysToPass: "1 day",
    platforms: ["TradeSea", "NinjaTrader", "Tradovate", "TradingView", "MotiveWave", "Quantower", "Sierra Chart", "Jigsaw", "Bookmap", "ATAS", "R|Trader Pro", "MultiCharts"], trustpilotRating: 4.7,
    products: [
      {
        name: "LucidFlex", badge: "No DLL · No funded consistency · Scaling plan · 5 payouts to live",
        params: [["Profit Target","$1.25K / $3K / $6K / $9K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","50% eval / None funded"],["Max Position Size","2 / 4 / 6 / 10 minis by size"],["Min Trading Days","2 eval / 5 winning days funded"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "Sale price", values: ["$70", "$98", "$157.50", "$294"] },
          { label: "Regular price", values: ["$100", "$140", "$225", "$420"] },
          { label: "Activation fee", values: ["None", "None", "None", "None"], color: C.textDim },
        ],
        evalTable: { title: "Evaluation", rows: [
          { label: "Profit target", values: ["$1,250", "$3,000", "$6,000", "$9,000"], color: C.green },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Max contracts", values: ["2", "4", "6", "10"], color: null },
          { label: "Consistency rule", values: ["50%", "50%", "50%", "50%"], color: null },
          { label: "Min trading days", values: ["2", "2", "2", "2"], color: null },
          { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
          { label: "Time limit", values: ["None", "None", "None", "None"], color: C.textDim },
        ]},
        fundedTable: { title: "LucidFlex Funded", rows: [
          { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Starting contracts", values: ["1", "2", "3", "4"], color: null },
          { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Winning days", values: ["5", "5", "5", "5"], color: null },
          { label: "Max payout", values: ["$1,000", "$2,000", "$2,500", "$3,000"], color: C.green },
          { label: "Profit split", values: ["90%", "90%", "90%", "90%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — trails highest end-of-day balance.",
              "MLL locks when profit reaches the drawdown amount; permanently locks at ~$100 above starting balance.",
              "After first payout: MLL permanently locks at the Locked MLL Balance.",
            ]},
            { bold: "Consistency Rule", lines: [
              "50% rule (eval only): best single day must be <50% of total profit.",
              "Enforces a minimum of 2 trading days to pass the evaluation.",
              "No consistency rule in funded phase.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Scaling plan active in funded — contract limits increase with simulated profits.",
              "Starting allocation: 1 mini ($25K) | 2 minis ($50K) | 3 minis ($100K) | 4 minis ($150K).",
              "Tier updates at END of each session — not real-time during trading day.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 5 payouts per funded account — moved to LucidLive after payout 5.",
              "Max 5 funded accounts across all Lucid types combined; max 10 evaluations at once.",
              "No time limit.",
            ]},
            { bold: "Unique Features", lines: [
              "LucidLive: after 5 payouts, transitioned to live capital — $0 start + one-time bonus ($1K/$2K/$3K/$4.5K by size). 90/10 split, daily payouts, EOD drawdown.",
              "Lucid may move traders to live earlier based on lifetime performance.",
              "LucidMaxx: invite-only daily payout program for proven traders — uncapped payouts, no DLL, instant live capital.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading fully allowed — no restrictions.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Microscalping prohibited: flagged if >50% of profits from trades held ≤5 seconds.",
              "No HFT.",
              "Automated trading (EAs, bots, trade copiers) allowed — trader responsible for software errors.",
              "DCA and scaling into trades allowed; martingaling discouraged but not banned.",
            ]},
            { bold: "Position Rules", lines: [
              "Hard close at 4:45 PM EST daily — positions auto-closed; does NOT breach account.",
              "On market holidays: close before market close regardless of the 4:45 PM rule.",
              "No overnight or weekend positions.",
              "Genuine scalping explicitly permitted.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts — including correlated instruments (e.g., long ES / short NQ in separate accounts).",
              "No account sharing.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90% to trader / 10% to firm.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "5 winning days required per payout cycle.",
              "Min daily profit to count as a winning day: $100 ($25K) | $150 ($50K) | $200 ($100K) | $250 ($150K).",
              "Must have at least $1 net profit during the current payout cycle.",
            ]},
            { bold: "Limits", lines: [
              "Min payout request: $500.",
              "Max payout: 50% of profits, capped at $1,000 (25K) / $2,000 (50K) / $2,500 (100K) / $3,000 (150K) per cycle.",
              "No buffer balance required.",
            ]},
            { bold: "Processing", lines: [
              "Processed within 2 business days after approval (often within minutes).",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — immediate liquidation and account closure.",
            ]},
            { bold: "Inactivity", lines: [
              "30-day inactivity — account permanently deleted.",
            ]},
            { bold: "Rule Violations", lines: [
              "Microscalping: >50% of profits from trades held ≤5 seconds (flag → review → warning → ban).",
              "Hedging across accounts — including correlated instruments.",
              "HFT.",
              "Account sharing.",
            ]},
          ]},
        ],
      },
      {
        name: "LucidPro", badge: "1-day pass · 40% funded · 3-day payout cycles · 5 payouts to live",
        params: [["Profit Target","$1.25K / $3K / $6K / $9K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None ($25K) / Scaling ($50K+)"],["Consistency Rule","None eval / 40% funded"],["Max Position Size","2 / 4 / 6 / 10 minis by size"],["Min Trading Days","None eval / None funded"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "Sale price", values: ["$94.50", "$129.50", "$199.50", "$259"] },
          { label: "Regular price", values: ["$135", "$185", "$285", "$370"] },
          { label: "Activation fee", values: ["None", "None", "None", "None"], color: C.textDim },
        ],
        evalTable: { title: "Evaluation", rows: [
          { label: "Profit target", values: ["$1,250", "$3,000", "$6,000", "$9,000"], color: C.green },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Max contracts", values: ["2", "4", "6", "10"], color: null },
          { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Min trading days", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
          { label: "Time limit", values: ["None", "None", "None", "None"], color: C.textDim },
        ]},
        fundedTable: { title: "LucidPro Funded", rows: [
          { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
          { label: "Fixed DLL", values: ["None", "$1,200", "$1,800", "$2,700"], color: null },
          { label: "Max contracts", values: ["2", "4", "6", "10"], color: null },
          { label: "Consistency rule", values: ["40%", "40%", "40%", "40%"], color: null },
          { label: "Min days", values: ["3", "3", "3", "3"], color: null },
          { label: "Max payout", values: ["$1,000 (tiered)", "$2,000 (tiered)", "$2,500 (tiered)", "$3,000 (tiered)"], color: C.green },
          { label: "Profit split", values: ["90%", "90%", "90%", "90%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown.",
              "MLL trails until it reaches starting balance, then locks permanently.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "Fixed DLL (eval + funded): None ($25K) | $1,200 ($50K) | $1,800 ($100K) | $2,700 ($150K).",
              "Scaling DLL (funded only): None ($25K) | 60% of peak EOD balance ($50K / $100K / $150K).",
              "DLL is soft — locks trading for the day, does NOT fail the account.",
            ]},
            { bold: "Consistency Rule", lines: [
              "No consistency rule during evaluation — can pass in one day.",
              "40% rule in funded: best day must be <40% of total profit per payout cycle.",
              "Consistency resets after each approved payout.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 5 payouts per account — LucidLive review after payout 5.",
              "Max 5 funded accounts across all Lucid types combined.",
              "No minimum trading days, no time limit.",
            ]},
            { bold: "Unique Features", lines: [
              "LucidLive: after 5 payouts, transitioned to live capital — $0 start + one-time bonus ($1K/$2K/$3K/$4.5K by size). 90/10 split, daily payouts, EOD drawdown.",
              "3-day payout cycles — payout requests available every 3 calendar days (weekends count).",
              "Lucid may move traders to live earlier based on lifetime performance.",
              "LucidMaxx: invite-only daily payout program for proven traders — uncapped payouts, no DLL, instant live capital.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading fully allowed — no restrictions.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Microscalping prohibited: flagged if >50% of profits from trades held ≤5 seconds.",
              "No HFT.",
              "Automated trading (EAs, bots, trade copiers) allowed.",
            ]},
            { bold: "Position Rules", lines: [
              "Hard close at 4:45 PM EST daily — positions auto-closed; does NOT breach account.",
              "No overnight or weekend positions.",
              "Scalping allowed.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts — including correlated instruments.",
              "No account sharing.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10 from payout 1.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout requests every 3 calendar days (weekends count).",
              "40% consistency rule must be met each cycle.",
              "Min profit goal per cycle: $250 (25K) / $500 (50K) / $750 (100K) / $1,000 (150K) — resets after each payout.",
              "No minimum profitable days required.",
            ]},
            { bold: "Limits", lines: [
              "Min payout request: $500.",
              "Payout caps — $25K: $1,000 (payout 1) / $1,500 (payouts 2+). $50K: $2,000 (payout 1) / $2,500 (payouts 2+). $100K: $2,500 (payout 1) / $3,000 (payouts 2+). $150K: $3,000 (payout 1) / $3,500 (payouts 2+).",
              "Buffer: balance must exceed MLL + $100 before payout is eligible. $25K: $26,100 | $50K: $52,100 | $100K: $103,100 | $150K: $154,600.",
            ]},
            { bold: "Processing", lines: [
              "Processed within 2 business days after approval (often within minutes).",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — immediate liquidation.",
            ]},
            { bold: "Inactivity", lines: [
              "30-day inactivity — account permanently deleted.",
            ]},
            { bold: "Rule Violations", lines: [
              "Microscalping: >50% of profits from trades held ≤5 seconds.",
              "Hedging across accounts.",
              "HFT.",
              "Account sharing.",
            ]},
          ]},
        ],
      },
      {
        name: "LucidDirect", badge: "Straight-to-funded · No eval · 20% consistency · DLL",
        params: [["Profit Target","None (straight to funded)"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None ($25K) / Scaling ($50K+)"],["Consistency Rule","20% funded"],["Max Position Size","2 / 4 / 6 / 10 minis by size"],["Min Trading Days","None"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "Sale price", values: ["~$238", "~$364", "~$490", "~$588"] },
          { label: "Regular price", values: ["$340", "$520", "$700", "$840"] },
          { label: "Activation fee", values: ["None", "None", "None", "None"], color: C.textDim },
        ],
        evalTable: null,
        fundedTable: { title: "LucidDirect Funded", rows: [
          { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,500", "$5,000"], color: C.red },
          { label: "Fixed DLL", values: ["None", "$1,200", "$2,100", "$3,000"], color: null },
          { label: "Max contracts", values: ["2", "4", "6", "10"], color: null },
          { label: "Consistency rule", values: ["20%", "20%", "20%", "20%"], color: null },
          { label: "Max payouts", values: ["6", "6", "6", "6"], color: null },
          { label: "Max payout", values: ["$1,000", "$2,000 (tiered)", "$2,500 (tiered)", "$3,000 (tiered)"], color: C.green },
          { label: "Profit split", values: ["90%", "90%", "90%", "90%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown.",
              "MLL is larger than LucidFlex/LucidPro: $100K = $3,500 (vs $3,000) | $150K = $5,000 (vs $4,500).",
              "MLL trails until it reaches starting balance, then locks permanently.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "Fixed DLL: None ($25K) | $1,200 ($50K) | $2,100 ($100K) | $3,000 ($150K).",
              "Scaling DLL: None ($25K) | 60% of peak EOD balance ($50K / $100K / $150K).",
              "DLL is soft — locks trading for the day, does NOT fail the account.",
            ]},
            { bold: "Consistency Rule", lines: [
              "20% consistency rule: best day must be <20% of total profits — strictest of any Lucid product.",
              "No evaluation phase — consistency applies from day one of funded trading.",
            ]},
            { bold: "Account Limits", lines: [
              "No evaluation — straight to funded.",
              "Maximum 6 payouts per account — LucidLive review after payout 6.",
              "Max 5 funded accounts across all Lucid types combined.",
              "No minimum trading days required (8-day requirement removed Feb 2026).",
            ]},
            { bold: "Unique Features", lines: [
              "LucidLive: after 6 payouts, transitioned to live capital — $0 start + one-time bonus ($1K/$2K/$3K/$4.5K by size). 90/10 split, daily payouts, EOD drawdown.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading fully allowed — no restrictions.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Microscalping prohibited: flagged if >50% of profits from trades held ≤5 seconds.",
              "No HFT.",
              "Automated trading (EAs, bots, trade copiers) allowed.",
            ]},
            { bold: "Position Rules", lines: [
              "Hard close at 4:45 PM EST daily — positions auto-closed; does NOT breach account.",
              "No overnight or weekend positions.",
              "Scalping allowed.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts — including correlated instruments.",
              "No account sharing.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10 from payout 1.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "No fixed payout window — request anytime after criteria are met.",
              "20% consistency must be met before requesting payout.",
              "Profit goals are tiered by cycle number and account size.",
            ]},
            { bold: "Limits", lines: [
              "Min payout request: $500.",
              "Payout caps — $25K: $1,000 (payouts 1–6). $50K: $2,000 (payouts 1–3) / $2,500 (payouts 4–6). $100K: $2,500 (payouts 1–3) / $3,000 (payouts 4–6). $150K: $3,000 (payouts 1–3) / $3,500 (payouts 4–6).",
            ]},
            { bold: "Processing", lines: [
              "Processed within 2 business days after approval (often within minutes).",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — immediate liquidation.",
            ]},
            { bold: "Inactivity", lines: [
              "30-day inactivity — account permanently deleted.",
            ]},
            { bold: "Rule Violations", lines: [
              "Microscalping: >50% of profits from trades held ≤5 seconds.",
              "Hedging across accounts.",
              "HFT.",
              "Account sharing.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "tpt", name: "Take Profit Trader", tagline: "Unique 3-stage path. Hard 5 PM close. Real CME live.",
    bestFor: "Traders pursuing real CME live with daily payouts and a strict 5 PM close.",
    logo: "https://www.google.com/s2/favicons?domain=takeprofittrader.com&sz=64",
    founded: "2021", location: "USA", regulated: false, website: "takeprofittrader.com",
    drawdownType: "Test: EOD → PRO: Intraday → PRO+: EOD",
    profitSplit: "80% (PRO) / 90% (PRO+)",
    newsTrading: false, overnightAllowed: false, hasDLL: false, maxFundedAccts: "5 PRO/PRO+ combined",
    maxPayout: "No cap (PRO)", daysToPass: "5 days",
    platforms: ["NinjaTrader", "Tradovate", "TradingView", "Quantower", "MotiveWave", "VolFix", "Bookmap", "Investor/RT", "Jigsaw Daytradr", "MultiCharts", "Trade Navigator", "Finamark", "R Trader", "ATAS Orderflow Trading", "Sierra Chart"], trustpilotRating: 4.4,
    products: [
      {
        name: "Test Account", badge: "Evaluation · EOD drawdown · News allowed",
        params: [["Profit Target","$1.5K / $3K / $4.5K / $6K / $9K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","50% eval"],["Max Position Size","3 / 6 / 9 / 12 / 15 by size"],["Min Trading Days","5 minimum"]],
        sizes: ["25K", "50K", "75K", "100K", "150K"],
        pricing: [
          { label: "Sale price", values: ["~$90", "~$102", "~$147", "~$198", "~$216"] },
          { label: "Regular price", values: ["$150", "$170", "$245", "$330", "$360"] },
          { label: "PRO activation fee", values: ["$130", "$130", "$130", "$130", "$130"] },
        ],
        evalTable: {
          title: "Test (Evaluation)",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000", "$4,500", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (EOD)", values: ["$1,500", "$2,000", "$3,000", "$4,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["3", "6", "9", "12", "15"], color: null },
            { label: "Consistency rule", values: ["50%", "50%", "50%", "50%", "50%"], color: null },
            { label: "Min trading days", values: ["5", "5", "5", "5", "5"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None", "None", "None", "None", "None"], color: C.textDim },
          ],
        },
        fundedTable: null,
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — only updates at 5 PM ET end of day.",
              "Intraday dips that recover before 5 PM do not move the drawdown floor.",
              "Drawdown locks permanently once it reaches starting balance.",
              "No Daily Loss Limit.",
            ]},
            { bold: "Consistency Rule", lines: [
              "50% rule: no single day can be 50% or more of total net profit.",
              "Violation does NOT fail the account — profit target auto-adjusts upward (Net P&L × 2).",
              "Minimum 5 trading days required to pass.",
            ]},
            { bold: "Account Limits", lines: [
              "News trading fully allowed during Test phase.",
              "Unlimited Test accounts open simultaneously.",
              "Test reset fee: $100 at any time, no limit on resets.",
            ]},
            { bold: "Unique Features", lines: [
              "Must pass Test to unlock PRO Account.",
              "PRO activation fee: $130 one-time (often waived with promo codes).",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "All news events allowed during Test phase — no restrictions.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 5:00 PM ET — holding past 5 PM causes immediate account termination.",
              "Trading reopens at 6 PM ET; trades after 6 PM count toward the next trading day.",
              "No counter positions (hedging same or closely related products).",
              "No bots or automated trading — manual only.",
              "Copy trading allowed on Test accounts.",
              "Must exit all positions before CME daily price limit is hit.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Holding any position past 5:00 PM ET — immediate termination, no exceptions.",
              "Balance touching or falling below the EOD drawdown floor.",
              "Failure to exit before CME daily price limit is hit.",
            ]},
            { bold: "Rule Violations", lines: [
              "Counter positions (hedging) — immediate violation.",
            ]},
          ]},
        ],
      },
      {
        name: "PRO Account", badge: "Funded sim · Intraday trailing · 80% · Daily payouts",
        params: [["Profit Target","None (withdraw anytime above buffer)"],["Max Loss Limit","INTRADAY Trailing (real-time HWM)"],["Daily Loss Limit","None"],["Consistency Rule","None"],["Max Position Size","3 / 6 / 9 / 12 / 15 by size"],["Min Trading Days","1 trade/week minimum"]],
        sizes: ["25K", "50K", "75K", "100K", "150K"],
        pricing: [
          { label: "PRO activation fee", values: ["$130", "$130", "$130", "$130", "$130"] },
        ],
        evalTable: null,
        fundedTable: {
          title: "PRO (Sim Funded)",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (Intraday)", values: ["$1,500", "$2,000", "$3,000", "$4,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["3", "6", "9", "12", "15"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Max payout", values: ["No cap", "No cap", "No cap", "No cap", "No cap"], color: C.green },
            { label: "Profit split", values: ["80%", "80%", "80%", "80%", "80%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "INTRADAY trailing drawdown — trails peak balance including unrealized P&L in real time.",
              "Drawdown locks permanently once it reaches starting balance — never moves again.",
              "Balance touching or falling below drawdown at any time = immediate liquidation.",
              "No Daily Loss Limit.",
            ]},
            { bold: "Buffer Zone", lines: [
              "Buffer = max drawdown amount. Must exceed buffer before withdrawing at 80%.",
              "Buffer by size: $26,500 (25K) / $52,000 (50K) / $77,500 (75K) / $103,000 (100K) / $154,500 (150K).",
              "Early closure buffer return: 50% if under 60 trading days, 80% if 60+ trading days.",
              "$5,000 of profit frozen as collateral when invited to PRO+.",
              "Max daily profit: $10,000.",
            ]},
            { bold: "Account Limits", lines: [
              "No profit target — withdraw anytime once above the buffer.",
              "No consistency rule.",
              "Must place at least 1 round-trip trade per calendar week or account is forfeited.",
              "Max 5 PRO/PRO+ accounts simultaneously (combined total).",
              "Copy trading allowed across up to 5 accounts.",
            ]},
            { bold: "Unique Features", lines: [
              "No evaluation — instant funded start after passing Test Account.",
              "Can reset PRO up to 3 times (reset fees ~$399–$999 by size).",
              "PRO+ invitation based on holistic performance: consistency, risk management, execution patterns. No single threshold.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "1-minute window: no open positions or orders 1 min before AND 1 min after FOMC, NFP, and CPI.",
              "Instrument-specific: no CL positions during Crude Oil Inventories; no 10Y/30Y positions during Bond Auctions.",
              "FED speakers and FOMC meeting minutes are allowed.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 5:00 PM ET — immediate termination if held past.",
              "No counter positions (hedging) — immediate violation.",
              "No bots or automated trading — manual only.",
              "Must exit all positions before CME price limit is hit.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20 split — trader keeps 80%.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Withdraw from day one — no payout windows, no delays.",
              "No minimum profitable days required.",
              "Buffer zone must be exceeded before withdrawing.",
            ]},
            { bold: "Limits", lines: [
              "No payout caps — withdraw any amount above the buffer.",
              "Buffer zone: $26,500 (25K) / $52,000 (50K) / $77,500 (75K) / $103,000 (100K) / $154,500 (150K).",
              "Max daily profit: $10,000.",
              "Withdrawal fee: free if over $250, $50 fee if $250 or under.",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Plaid (US banks, instant/ACH), PayPal, Wise (international).",
              "Processing: typically within 24 hours.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Holding any position past 5:00 PM ET — immediate termination.",
              "Balance touching or falling below the intraday drawdown threshold.",
              "Failure to exit before CME price limit hit.",
            ]},
            { bold: "Inactivity", lines: [
              "Missing a full calendar week without a round-trip trade — account forfeited.",
            ]},
            { bold: "Rule Violations", lines: [
              "Trading during prohibited news events (1 min before/after FOMC, NFP, CPI).",
              "Counter positions (hedging) — immediate violation.",
              "Using bots or automated trading.",
            ]},
          ]},
        ],
      },
      {
        name: "PRO+ Account", badge: "Real CME live · EOD drawdown · 90% · No buffer",
        params: [["Profit Target","None"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","None"],["Max Position Size","3 / 6 / 9 / 12 / 15 by size"],["Min Trading Days","1 trade/week minimum"]],
        sizes: ["25K", "50K", "75K", "100K", "150K"],
        pricing: [],
        evalTable: null,
        fundedTable: {
          title: "PRO+ (Live Funded)",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (EOD)", values: ["$1,500", "$2,000", "$3,000", "$4,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["3", "6", "9", "12", "15"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None", "None", "None"], color: C.textDim },
            { label: "Max payout", values: ["No cap", "No cap", "No cap", "No cap", "No cap"], color: C.green },
            { label: "Profit split", values: ["90%", "90%", "90%", "90%", "90%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — reverts from PRO's intraday trailing to end-of-day, making it easier on live capital.",
              "Drawdown locks permanently once it reaches starting balance ($0).",
              "No Daily Loss Limit.",
              "No buffer zone — withdraw immediately from day one.",
            ]},
            { bold: "Account Limits", lines: [
              "No profit target.",
              "No consistency rule.",
              "Must place at least 1 round-trip trade per calendar week.",
              "Max 5 PRO/PRO+ accounts simultaneously (combined total).",
              "Copy trading allowed for up to 5 accounts.",
            ]},
            { bold: "Unique Features", lines: [
              "Invitation only — TPT decides based on holistic performance review. Cannot purchase directly.",
              "Routes directly to live CME exchange — real capital, real fills.",
              "Setup: 1–3 business days after invitation.",
              "$5,000 frozen from PRO account used as collateral — no additional cost for upgrade.",
              "PRO account placed on hold when PRO+ activates; all trading and withdrawals move to PRO+ only.",
              "If PRO+ is lost: remaining profits and trader's share of frozen $5K eligible for withdrawal. Negative balance deducted from $5K.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "Same 1-minute window as PRO: no open positions 1 min before AND 1 min after FOMC, NFP, and CPI.",
              "Instrument-specific: no CL during Crude Oil Inventories; no 10Y/30Y during Bond Auctions.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 5:00 PM ET — same termination rule as PRO.",
              "No counter positions (hedging).",
              "No bots or automated trading — manual only.",
              "Must exit all positions before CME price limit is hit.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10 split — trader keeps 90%.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "No buffer zone — withdraw from day one with no restrictions.",
              "No minimum profitable days. No payout caps.",
            ]},
            { bold: "Limits", lines: [
              "No maximum withdrawal amount.",
              "Withdrawal fee: free if over $250, $50 fee if $250 or under.",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Plaid (US banks), PayPal, Wise.",
              "Processing: typically within 24 hours.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Holding any position past 5:00 PM ET — immediate termination.",
              "Balance touching or falling below the EOD drawdown floor.",
              "Failure to exit before CME price limit hit.",
            ]},
            { bold: "Inactivity", lines: [
              "Missing a full calendar week without a round-trip trade.",
            ]},
            { bold: "Rule Violations", lines: [
              "Trading during prohibited news events.",
              "Counter positions (hedging).",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "fundednext", name: "FundedNext Futures", tagline: "24-hr payout guarantee. News allowed. Three distinct paths.",
    bestFor: "Traders who want news freedom, 24-hour payout guarantee, and no activation fees.",
    logo: "https://www.google.com/s2/favicons?domain=fundednext.com&sz=64",
    founded: "2025", location: "UAE", regulated: false, website: "fundednext.com",
    drawdownType: "EOD Trailing", profitSplit: "80%",
    newsTrading: true, overnightAllowed: false, hasDLL: true, maxFundedAccts: "Up to $1.2M total simulated capital",
    maxPayout: "$6,000 (Legacy 100K)", daysToPass: "1 day (Rapid)",
    platforms: ["Tradovate", "NinjaTrader", "TradingView"], trustpilotRating: 4.5,
    products: [
      {
        name: "Bolt", badge: "$50K only · 5 payout max · DLL · No benchmark days",
        params: [["Profit Target","$3,000"],["Max Loss Limit","$2K EOD Trailing"],["Daily Loss Limit","$1,000 (soft)"],["Consistency Rule","40% eval / None funded"],["Max Position Size","3 minis / 9 micros"],["Min Trading Days","3 eval / None funded"]],
        sizes: ["50K"],
        pricing: [
          { label: "One-time fee", values: ["$99.99"] },
          { label: "Activation fee", values: ["None"], color: C.textDim },
        ],
        evalTable: { title: "Evaluation", rows: [
          { label: "Profit target", values: ["$3,000"], color: C.green },
          { label: "Max drawdown", values: ["$2,000"], color: C.red },
          { label: "Daily loss limit", values: ["$1,000"], color: null },
          { label: "Max contracts", values: ["3"], color: null },
          { label: "Consistency rule", values: ["40%"], color: null },
          { label: "Min trading days", values: ["3"], color: null },
          { label: "News trading", values: ["Allowed"], color: C.green },
          { label: "Time limit", values: ["None"], color: C.textDim },
        ]},
        fundedTable: { title: "Funded Account", rows: [
          { label: "Profit target", values: ["None"], color: C.textDim },
          { label: "Max drawdown", values: ["$2,000"], color: C.red },
          { label: "Daily loss limit", values: ["$1,000"], color: null },
          { label: "Max contracts", values: ["3"], color: null },
          { label: "Consistency rule", values: ["None"], color: C.textDim },
          { label: "Max payouts", values: ["5"], color: null },
          { label: "Max payout", values: ["$1,200 (tiered)"], color: C.green },
          { label: "Profit split", values: ["80%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — updates once per day based on highest end-of-day balance.",
              "MLL locks permanently at $50,100 once profits equal the drawdown amount.",
              "If floating loss pushes equity below MLL during an active trade = INSTANT hard breach.",
              "After first payout: MLL resets to $50,100 permanently.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "DLL: $1,000 SOFT — locks trading for the day, does NOT fail the account.",
              "Auto-restores at the start of next session.",
              "Applies in both challenge and funded phases.",
            ]},
            { bold: "Consistency Rule", lines: [
              "40% consistency rule applies during evaluation only — single day profit capped at 40% of total.",
              "If limit is exceeded, target adjusts upward rather than failing the account.",
              "No consistency rule in funded phase.",
            ]},
            { bold: "Account Limits", lines: [
              "Single size: $50K only.",
              "Max 3 E-mini contracts / 9 Micro E-mini (1:3 ratio).",
              "No time limit. Minimum 3 trading days in evaluation.",
              "News trading fully ALLOWED.",
            ]},
            { bold: "Unique Features", lines: [
              "Maximum 5 total payouts — account closes after 5th payout.",
              "No benchmark days required for first 4 payouts; 5th payout is final settlement.",
              "Buffer: must have EOD balance of $52,100+ before withdrawing.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading fully ALLOWED — no restrictions, no time windows.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No reverse hedging across FundedNext accounts.",
              "No correlated instrument hedging (e.g., long ES and short YM simultaneously).",
              "No grid trading, spoofing, layering, or wash trading.",
              "No platform error exploitation.",
              "No multi-order DOM spam.",
              "Copy trading from another person's signals or group = PROHIBITED.",
              "No account rolling.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 3:10 PM CT — auto-liquidated if held past close.",
              "Trading resumes at 5:00 PM CT; after weekend: Sunday 5:00 PM CT.",
              "No account sharing — one trader per account.",
              "Copy trading within your OWN FundedNext accounts = ALLOWED.",
            ]},
            { bold: "Market Rules", lines: [
              "Cannot trade within 2% of CME daily price limit — risk of account suspension.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20 profit split.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "24-hour payout guarantee — $1,000 compensation if missed.",
              "No benchmark days required for first 4 payouts.",
              "Buffer: EOD balance must be $52,100+ before withdrawing.",
            ]},
            { bold: "Limits", lines: [
              "Min withdrawal: $250 | Max withdrawal: $1,200 per payout (payouts 1–4).",
              "5th payout (final settlement): up to $7,700 including buffer. Profit above $7,700 forfeited.",
              "Maximum 5 total payouts — account concludes after 5th.",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Crypto, Riseworks.",
              "24-hour processing guarantee.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — instant hard breach.",
              "Withdrawing 100% of simulated profit in a single request — hard breach.",
              "Holding positions past 3:10 PM CT — auto-liquidated.",
            ]},
            { bold: "Inactivity", lines: [
              "7-day inactivity on challenge accounts — breached and deactivated.",
              "30-day inactivity on funded accounts — deactivated.",
            ]},
            { bold: "Rule Violations", lines: [
              "Reverse hedging across accounts.",
              "Account rolling.",
              "Trading within 2% of CME price limit.",
            ]},
          ]},
        ],
      },
      {
        name: "Rapid", badge: "No eval consistency · 40% funded · Every 3 days · Caps removed at 5",
        params: [["Profit Target","$1.5K / $3K / $5K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","None eval / 40% funded"],["Max Position Size","2 / 3 / 5 minis eval · 3 / 5 / 7 minis funded"],["Min Trading Days","None eval / None funded"]],
        sizes: ["25K", "50K", "100K"],
        pricing: [
          { label: "One-time fee", values: ["$99.99", "$199.99", "$279.99"] },
          { label: "Activation fee", values: ["None", "None", "None"], color: C.textDim },
        ],
        evalTable: { title: "Evaluation", rows: [
          { label: "Profit target", values: ["$1,500", "$3,000", "$5,000"], color: C.green },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$2,500"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max contracts", values: ["2", "3", "5"], color: null },
          { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
          { label: "Min trading days", values: ["None", "None", "None"], color: C.textDim },
          { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
          { label: "Time limit", values: ["None", "None", "None"], color: C.textDim },
        ]},
        fundedTable: { title: "Funded Account", rows: [
          { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$2,500"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max contracts", values: ["3", "5", "7"], color: null },
          { label: "Consistency rule", values: ["40%", "40%", "40%"], color: null },
          { label: "Min trading days", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max payout", values: ["$800 (tiered)", "$1,500 (tiered)", "$2,500 (tiered)"], color: C.green },
          { label: "Profit split", values: ["80%", "80%", "80%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — updates once per day based on highest end-of-day balance.",
              "MLL locks permanently at starting balance once profits equal the drawdown amount.",
              "If floating loss pushes equity below MLL = INSTANT hard breach.",
              "After first payout: MLL resets to starting balance permanently.",
            ]},
            { bold: "Consistency Rule", lines: [
              "No consistency rule during evaluation.",
              "40% consistency rule applies in funded phase — daily profit cannot exceed 40% of total profits in the current payout cycle.",
              "Consistency must be met within each payout cycle to be eligible for withdrawal.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Funded contracts scale UP from eval — more contracts in funded than during evaluation.",
              "Eval: 2 / 3 / 5 minis by size. Funded: 3 / 5 / 7 minis by size.",
            ]},
            { bold: "Account Limits", lines: [
              "Three sizes: $25K | $50K | $100K.",
              "No Daily Loss Limit.",
              "No minimum trading days. No time limit.",
              "News trading fully ALLOWED.",
            ]},
            { bold: "Unique Features", lines: [
              "No benchmark days required.",
              "Payout every 3 days.",
              "After 5 payouts: all caps are removed — unlimited withdrawals thereafter.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading fully ALLOWED — no restrictions, no time windows.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No reverse hedging across FundedNext accounts.",
              "No correlated instrument hedging.",
              "No grid trading, spoofing, layering, or wash trading.",
              "No platform error exploitation.",
              "No multi-order DOM spam.",
              "Copy trading from another person's signals or group = PROHIBITED.",
              "No account rolling.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 3:10 PM CT — auto-liquidated if held past close.",
              "Trading resumes at 5:00 PM CT; after weekend: Sunday 5:00 PM CT.",
              "No account sharing.",
              "Copy trading within your OWN FundedNext accounts = ALLOWED.",
            ]},
            { bold: "Market Rules", lines: [
              "Cannot trade within 2% of CME daily price limit.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20 profit split.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "24-hour payout guarantee — $1,000 compensation if missed.",
              "Withdrawal frequency: every 3 days.",
              "No benchmark days required.",
              "40% consistency rule must be satisfied within the current payout cycle.",
            ]},
            { bold: "Limits", lines: [
              "Min withdrawal: $250 ($25K/$50K) | $500 ($100K).",
              "Max payout per cycle: $800 ($25K) | $1,500 ($50K) | $2,500 ($100K).",
              "After 5 payouts: payout cap removed — unlimited withdrawals.",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Crypto, Riseworks.",
              "24-hour processing guarantee.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — instant hard breach.",
              "Withdrawing 100% of simulated profit in a single request — hard breach.",
              "Holding positions past 3:10 PM CT — auto-liquidated.",
            ]},
            { bold: "Inactivity", lines: [
              "7-day inactivity on challenge accounts — breached and deactivated.",
              "30-day inactivity on funded accounts — deactivated.",
            ]},
            { bold: "Rule Violations", lines: [
              "Exceeding the 40% daily profit cap in a funded cycle.",
              "Reverse hedging across accounts.",
              "Trading within 2% of CME price limit.",
            ]},
          ]},
        ],
      },
      {
        name: "Legacy", badge: "5 benchmark days · No funded consistency · Full unlock at 30",
        params: [["Profit Target","$1.25K / $2.5K / $6K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","40% eval / None funded"],["Max Position Size","2 / 3 / 5 minis eval · 3 / 5 / 7 minis funded"],["Min Trading Days","3 eval / 5 benchmark days funded"]],
        sizes: ["25K", "50K", "100K"],
        pricing: [
          { label: "One-time fee", values: ["$79.99", "$149.99", "$249.99"] },
          { label: "Activation fee", values: ["None", "None", "None"], color: C.textDim },
        ],
        evalTable: { title: "Evaluation", rows: [
          { label: "Profit target", values: ["$1,250", "$3,000", "$6,000"], color: C.green },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max contracts", values: ["2", "3", "5"], color: null },
          { label: "Consistency rule", values: ["40%", "40%", "40%"], color: null },
          { label: "Min trading days", values: ["3", "3", "3"], color: null },
          { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
          { label: "Time limit", values: ["None", "None", "None"], color: C.textDim },
        ]},
        fundedTable: { title: "Funded Account", rows: [
          { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max contracts", values: ["3", "5", "7"], color: null },
          { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
          { label: "Benchmark days", values: ["5", "5", "5"], color: null },
          { label: "Max payout", values: ["$3,000", "$6,000", "$6,000"], color: C.green },
          { label: "Profit split", values: ["80%", "80%", "80%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — updates once per day based on highest end-of-day balance.",
              "MLL locks permanently at starting balance once profits equal the drawdown amount.",
              "If floating loss pushes equity below MLL = INSTANT hard breach.",
              "After first payout: MLL resets to starting balance permanently.",
            ]},
            { bold: "Consistency Rule", lines: [
              "40% consistency rule applies during evaluation — no single day can exceed 40% of total profit.",
              "No consistency rule in funded phase.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Funded contracts scale UP from eval.",
              "Eval: 2 / 3 / 5 minis by size. Funded: 3 / 5 / 7 minis by size.",
            ]},
            { bold: "Account Limits", lines: [
              "Three sizes: $25K | $50K | $100K.",
              "No Daily Loss Limit.",
              "Minimum 3 trading days in evaluation. No time limit.",
              "News trading fully ALLOWED.",
            ]},
            { bold: "Unique Features", lines: [
              "5 benchmark days required before first withdrawal.",
              "Min daily profit for a benchmark day: $100 ($25K) | $200 ($50K/$100K).",
              "Before 30 benchmark days: max 50% of profits per payout, capped at $3K ($25K) | $6K ($50K/$100K).",
              "After 30 benchmark days: 100% of profit, ALL caps removed.",
              "Micro-scalping monitored — consistent flash-in/flash-out patterns are reviewed.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading fully ALLOWED — no restrictions, no time windows.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No reverse hedging across FundedNext accounts.",
              "No correlated instrument hedging.",
              "No grid trading, spoofing, layering, or wash trading.",
              "No platform error exploitation.",
              "No multi-order DOM spam.",
              "Copy trading from another person's signals or group = PROHIBITED.",
              "No account rolling.",
              "Micro-scalping monitored — consistent flash-in/flash-out patterns reviewed.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 3:10 PM CT — auto-liquidated if held past close.",
              "Trading resumes at 5:00 PM CT; after weekend: Sunday 5:00 PM CT.",
              "No account sharing.",
              "Copy trading within your OWN FundedNext accounts = ALLOWED.",
            ]},
            { bold: "Market Rules", lines: [
              "Cannot trade within 2% of CME daily price limit.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20 profit split.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "24-hour payout guarantee — $1,000 compensation if missed.",
              "Withdrawal frequency: every 5 days.",
              "5 benchmark days required before first payout.",
              "Min daily profit per benchmark day: $100 ($25K) | $200 ($50K/$100K).",
              "Min profit between payouts: $500.",
            ]},
            { bold: "Limits", lines: [
              "Before 30 benchmark days: max 50% of profits up to $3K ($25K) | $6K ($50K/$100K).",
              "After 30 benchmark days: 100% of profit, ALL caps removed.",
              "Min withdrawal: $250.",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Crypto, Riseworks.",
              "24-hour processing guarantee.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below MLL — instant hard breach.",
              "Withdrawing 100% of simulated profit in a single request — hard breach.",
              "Holding positions past 3:10 PM CT — auto-liquidated.",
            ]},
            { bold: "Inactivity", lines: [
              "7-day inactivity on challenge accounts — breached and deactivated.",
              "30-day inactivity on funded accounts — deactivated.",
            ]},
            { bold: "Rule Violations", lines: [
              "Reverse hedging across accounts.",
              "Trading within 2% of CME price limit.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "topone", name: "Top One Futures", tagline: "US-based. Five funded paths. Under 4-hour payouts.",
    bestFor: "Traders who want instant funding options, fast payouts, and aggressive multi-account scaling.",
    logo: "https://www.google.com/s2/favicons?domain=toponefutures.com&sz=64",
    founded: "2025", location: "USA", regulated: false, website: "toponefutures.com",
    drawdownType: "EOD Trailing (Intraday on S2F PRO)", profitSplit: "100% first $20K / 90% after",
    newsTrading: true, overnightAllowed: false, hasDLL: true, maxFundedAccts: "Up to 23 total (3 Elite/Instant + 10 S2F PRO + 10 Ignite)",
    maxPayout: "$3,500 (Elite 150K)", daysToPass: "1 day",
    platforms: ["NinjaTrader", "TradingView", "Tradovate", "Project X", "Rithmic", "Quantower"], trustpilotRating: 4.8,
    products: [
      {
        name: "Elite", badge: "1-step eval · 25% funded consistency · $149 activation",
        params: [["Profit Target","$1.5K / $3K / $6K / $9K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","$625 / $1,250 / $2,500 / $3,750 (soft)"],["Consistency Rule","None eval / 25% funded"],["Max Position Size","1 / 3 / 5 / 7 minis by size"],["Min Trading Days","None eval / 10 active days between payouts"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "One-time (sale)", values: ["~$41", "~$63", "~$125", "~$185"] },
          { label: "One-time (regular)", values: ["$69", "$105", "$209", "$309"] },
          { label: "Activation fee", values: ["$149", "$149", "$149", "$149"] },
          { label: "Reset fee", values: ["$39", "$59", "$109", "$169"] },
        ],
        evalTable: { title: "Evaluation", rows: [
          { label: "Profit target", values: ["$1,500", "$3,000", "$6,000", "$9,000"], color: C.green },
          { label: "Max drawdown", values: ["$1,500", "$2,000", "$3,000", "$4,500"], color: C.red },
          { label: "Daily loss limit", values: ["$625", "$1,250", "$2,500", "$3,750"], color: null },
          { label: "Max contracts", values: ["1", "3", "5", "7"], color: null },
          { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Min trading days", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
          { label: "Time limit", values: ["None", "None", "None", "None"], color: C.textDim },
        ]},
        fundedTable: { title: "Sim Funded", rows: [
          { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Max drawdown", values: ["$1,500", "$2,000", "$3,000", "$4,500"], color: C.red },
          { label: "Daily loss limit", values: ["$625", "$1,250", "$2,500", "$3,750"], color: null },
          { label: "Max contracts", values: ["1", "3", "5", "7"], color: null },
          { label: "Consistency rule", values: ["25%", "25%", "25%", "25%"], color: null },
          { label: "Active days", values: ["10", "10", "10", "10"], color: null },
          { label: "Max payout", values: ["$1,500", "$2,500", "$3,000", "$3,500"], color: C.green },
          { label: "Profit split", values: ["100%→90%", "100%→90%", "100%→90%", "100%→90%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — trails highest end-of-day balance.",
              "Locks at starting balance + $100 once profit reaches drawdown + $100, and again after first payout.",
              "Intraday breach: balance cannot drop below the drawdown floor at any point, even though it trails EOD.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "$625 (25K) / $1,250 (50K) / $2,500 (100K) / $3,750 (150K) — SOFT, pauses trading for the day, does NOT fail the account.",
            ]},
            { bold: "Consistency Rule", lines: [
              "None during challenge — no min trading days, no time limit. Can pass in 1 day.",
              "News trading ALLOWED during evaluation.",
              "25% rule in funded: best single day must be <25% of total profit. Resets after each payout.",
            ]},
            { bold: "Account Limits", lines: [
              "Max 3 Elite funded accounts simultaneously.",
              "One-time purchase — no recurring billing.",
              "Activation fee: $149 (all sizes). Reset fee: $39 / $59 / $109 / $169 by size.",
            ]},
            { bold: "Unique Features", lines: [
              "10 active trading days required between payouts.",
              "After 3 payouts → reviewed for live account.",
              "Live account: 80/20 split, daily payouts, min $200, MLL = 60% of account (static).",
              "Sim profits up to 10% of starting balance carried to live.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading ALLOWED — no restrictions.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No EAs, bots, or automated trading — manual only.",
              "10-second minimum trade duration.",
              "No account rolling or gambling behavior.",
              "No group trading or signal following.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 4:10 PM EST — forced liquidation if missed.",
              "No overnight or weekend positions.",
              "Trading hours: 6:00 PM – 4:10 PM EST.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts or correlated instruments.",
              "Copy trading allowed only between same account type AND same size.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "100% on first $20K withdrawn, then 90/10 after.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout targets: 6% (first) / 5% (second) / 4% (third+) of account balance.",
              "Must also hit $1,500 minimum profit target.",
              "25% consistency rule must be met before requesting.",
              "10 active trading days required between payouts.",
            ]},
            { bold: "Limits", lines: [
              "Min payout: $500.",
              "Max payout: $1,500 ($25K) | $2,500 ($50K) | $3,000 ($100K) | $3,500 ($150K).",
            ]},
            { bold: "Processing", lines: [
              "Payout processing: under 4 hours average.",
              "Payout methods: Crypto, Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below trailing drawdown at any time — hard breach.",
            ]},
            { bold: "Inactivity", lines: [
              "14-day inactivity on funded accounts — hard breach and permanent close.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using EAs, bots, or automated trading.",
              "Hedging across accounts.",
              "Account rolling.",
              "Trades held under 10 seconds consistently.",
            ]},
          ]},
        ],
      },
      {
        name: "Elite Daily", badge: "1-step eval · Daily payouts · No activation fee · No DLL · No funded consistency",
        params: [["Profit Target","$1.5K / $3K / $6K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","45% eval / None funded"],["Max Position Size","1 / 3 / 5 minis by size"],["Min Trading Days","3 eval / None funded"]],
        sizes: ["25K", "50K", "100K"],
        pricing: [
          { label: "Monthly (sale)", values: ["~$107/mo", "~$131/mo", "~$239/mo"] },
          { label: "Monthly (regular)", values: ["$178/mo", "$218/mo", "$398/mo"] },
          { label: "Activation fee", values: ["None", "None", "None"], color: C.textDim },
          { label: "Reset fee (eval)", values: ["$79", "$95", "$185"] },
          { label: "Reset fee (funded)", values: ["$299", "$499", "$849"] },
        ],
        evalTable: { title: "Evaluation", rows: [
          { label: "Profit target", values: ["$1,500", "$3,000", "$6,000"], color: C.green },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max contracts", values: ["1", "3", "5"], color: null },
          { label: "Consistency rule", values: ["45%", "45%", "45%"], color: null },
          { label: "Min trading days", values: ["3", "3", "3"], color: null },
          { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
          { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
        ]},
        fundedTable: { title: "Sim Funded (Daily)", rows: [
          { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$2,500"], color: C.red },
          { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
          { label: "Starting contracts", values: ["1", "1", "2"], color: null },
          { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
          { label: "Max payout", values: ["$750", "$1,000", "$1,500"], color: C.green },
          { label: "Profit split", values: ["90%", "90%", "90%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — trails highest end-of-day balance.",
              "Funded MLL on $100K is tighter than eval: $2,500 funded vs $3,000 eval.",
              "Locks at starting balance + $100 once profit threshold is met.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "No DLL on Elite Daily — evaluation or funded.",
            ]},
            { bold: "Consistency Rule", lines: [
              "45% consistency during evaluation: no single day >45% of total profit.",
              "No minimum trading days — can pass in 1 day. No time limit.",
              "News trading ALLOWED during evaluation.",
              "No consistency rule in funded phase.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Contract scaling active in funded phase only (not eval).",
              "Starting contracts: 1 ($25K) | 1→2→3 ($50K) | 2→3→4→5 ($100K).",
            ]},
            { bold: "Account Limits", lines: [
              "No activation fee. Max 5 Elite Daily accounts simultaneously.",
              "Reset fee (eval): $79 (25K) / $95 (50K) / $185 (100K).",
              "Reset fee (funded): $299 (25K) / $499 (50K) / $849 (100K).",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading ALLOWED during evaluation.",
              "News restricted on funded: no trades 2 minutes before/after high-impact releases.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No EAs, bots, or automated trading.",
              "10-second minimum trade duration.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 4:10 PM EST.",
              "No overnight or weekend positions.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts.",
              "Contract scaling applies in funded phase.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10 split.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Daily payouts — eligible from day 1 of funded.",
            ]},
            { bold: "Limits", lines: [
              "Min payout: $500.",
              "Max payout: $750 (25K) / $1,000 (50K) / $1,500 (100K).",
            ]},
            { bold: "Processing", lines: [
              "Payout processing: under 4 hours average.",
              "Payout methods: Crypto, Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below trailing drawdown — hard breach.",
            ]},
            { bold: "Inactivity", lines: [
              "14-day inactivity — hard breach.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using EAs or bots.",
              "Hedging across accounts.",
              "Funded news trading violations.",
            ]},
          ]},
        ],
      },
      {
        name: "Instant Sim Funded", badge: "No eval · EOD drawdown · 20% consistency · Full contracts",
        params: [["Profit Target","6% / 5% / 4% (tiered per payout)"],["Max Loss Limit","$1K / $2K / $4K / $6K EOD Trailing"],["Daily Loss Limit","$625 / $1,250 / $2,500 / $3,750 (soft)"],["Consistency Rule","20% funded"],["Max Position Size","1 / 3 / 5 / 7 minis by size"],["Min Trading Days","10 active days between payouts"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "One-time (sale)", values: ["~$210", "~$340", "~$411", "~$470"] },
          { label: "One-time (regular)", values: ["$419", "$679", "$821", "$939"] },
        ],
        evalTable: null,
        fundedTable: { title: "Instant Sim Funded", rows: [
          { label: "Payout target", values: ["6%→4%", "6%→4%", "6%→4%", "6%→4%"], color: null },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$4,000", "$6,000"], color: C.red },
          { label: "Daily loss limit", values: ["$625", "$1,250", "$2,500", "$3,750"], color: null },
          { label: "Max contracts", values: ["1", "3", "5", "7"], color: null },
          { label: "Consistency rule", values: ["20%", "20%", "20%", "20%"], color: null },
          { label: "Active days", values: ["10", "10", "10", "10"], color: null },
          { label: "Max payout", values: ["$1,500", "$2,500", "$3,000", "$3,500"], color: C.green },
          { label: "Profit split", values: ["100%→90%", "100%→90%", "100%→90%", "100%→90%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — trails highest end-of-day balance.",
              "Balance cannot drop below the drawdown floor at any time, including intraday.",
              "Locks at starting balance + $100 once profit reaches drawdown + $100, and after first payout.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "$625 (25K) / $1,250 (50K) / $2,500 (100K) / $3,750 (150K) — SOFT, pauses trading for the day, does NOT fail the account.",
            ]},
            { bold: "Consistency Rule", lines: [
              "20% rule: best day must be <20% of total profit. Resets after each payout.",
            ]},
            { bold: "Account Limits", lines: [
              "No evaluation — immediate funded access.",
              "Max 5 Instant Sim accounts (25K/50K). Max 3 Instant Sim accounts (100K/150K). No reset option.",
              "One-time fee — no monthly billing.",
            ]},
            { bold: "Unique Features", lines: [
              "Payout targets are percentage-based: 6% (first) / 5% (second) / 4% (third+) of account balance.",
              "10 active trading days required between payouts.",
              "After 3 payouts → reviewed for live account.",
              "No contract scaling — full contracts from day one.",
              "Sim profits up to 10% of starting balance carried to live.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading ALLOWED — no restrictions.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No EAs, bots, or automated trading.",
              "10-second minimum trade duration.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 4:10 PM EST.",
              "No overnight or weekend positions.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts or correlated instruments.",
              "Copy trading allowed only between same account type AND same size.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "100% on first $20K withdrawn, then 90/10 after.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout targets: 6% (first) / 5% (second) / 4% (third+) of account balance.",
              "20% consistency rule must be met before requesting.",
              "10 active trading days required between payouts.",
            ]},
            { bold: "Limits", lines: [
              "Min payout: $500.",
              "Max payout: $1,500 ($25K) | $2,500 ($50K) | $3,000 ($100K) | $3,500 ($150K).",
            ]},
            { bold: "Processing", lines: [
              "Payout processing: under 4 hours average.",
              "Payout methods: Crypto, Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below trailing drawdown at any time — hard breach.",
            ]},
            { bold: "Inactivity", lines: [
              "14-day inactivity — hard breach.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using EAs or bots.",
              "Hedging across accounts.",
              "Account rolling.",
            ]},
          ]},
        ],
      },
      {
        name: "S2F Sim PRO", badge: "INTRADAY drawdown · 20% ESS · Copy up to 10 accounts · Fastest to live",
        params: [["Profit Target","6% / 5% (tiered per payout)"],["Max Loss Limit","$1K / $1.625K / $3.25K / $5K Intraday Trailing"],["Daily Loss Limit","$500 / $1K / $2K / $3K (soft)"],["Consistency Rule","20% ESS (funded)"],["Max Position Size","1 / 4 / 8 / 12 minis by size"],["Min Trading Days","10 days (7 profitable)"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "One-time (sale)", values: ["~$103", "~$168", "~$253", "~$291"] },
          { label: "One-time (regular)", values: ["$257", "$421", "$632", "$727"] },
        ],
        evalTable: null,
        fundedTable: { title: "S2F Sim PRO", rows: [
          { label: "Payout target", values: ["6%→5%", "6%→5%", "6%→5%", "6%→5%"], color: null },
          { label: "Max drawdown (Intraday)", values: ["$1,000", "$1,625", "$3,250", "$5,000"], color: C.red },
          { label: "Daily loss limit", values: ["$500", "$1,000", "$2,000", "$3,000"], color: null },
          { label: "Max contracts", values: ["1", "4", "8", "12"], color: null },
          { label: "Consistency rule", values: ["20% ESS", "20% ESS", "20% ESS", "20% ESS"], color: null },
          { label: "Trading days", values: ["10", "10", "10", "10"], color: null },
          { label: "Profitable days", values: ["7", "7", "7", "7"], color: null },
          { label: "Max payout", values: ["$1,500", "$2,500", "$3,000", "$3,500"], color: C.green },
          { label: "Profit split", values: ["100%→90%", "100%→90%", "100%→90%", "100%→90%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "INTRADAY trailing drawdown — updates in real time based on highest equity including unrealized P&L.",
              "Every new equity peak permanently raises the drawdown floor, even if the trade reverses and closes flat.",
              "25K: $1,000 | 50K: $1,625 | 100K: $3,250 | 150K: $5,000.",
              "Locks at starting balance + $100 after first payout.",
              "If equity falls below drawdown at any time = instant breach.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "$500 (25K) / $1,000 (50K) / $2,000 (100K) / $3,000 (150K) — SOFT, pauses trading for the session.",
            ]},
            { bold: "Consistency Rule (ESS)", lines: [
              "20% ESS (Equity Stability Score): best single-day profit must not exceed 20% of total profits.",
              "Formula: Best Day Profit / Total Profit × 100 must be ≤20%.",
              "ESS resets after every payout.",
            ]},
            { bold: "Account Limits", lines: [
              "No evaluation — immediate funded access.",
              "Max 10 S2F PRO accounts simultaneously. No reset option.",
              "One-time fee — no monthly billing. No contract scaling — full contracts from day one.",
              "Copy trading: PRO→PRO only, same size.",
            ]},
            { bold: "Unique Features", lines: [
              "10 trading days (7 must be profitable) required before each payout.",
              "Profitable day = 0.1% of starting balance ($25 on $25K / $50 on $50K / $100 on $100K / $150 on $150K).",
              "After 2 payouts → reviewed for live (fastest live path of any Top One product).",
              "Sim profits up to 10% of starting balance carried to live.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading ALLOWED — no restrictions.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No EAs, bots, or automated trading.",
              "10-second minimum trade duration.",
              "Copying across different account types (PRO→Instant or PRO→Elite) is prohibited.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 4:00 PM EST — S2F PRO closes earlier than other Top One types.",
              "No overnight or weekend positions.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts.",
              "Copy trading PRO→PRO only, same size.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "100% on first $20K withdrawn, then 90/10 after.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Payout targets: 6% (first) / 5% (second+) of account balance.",
              "20% ESS must be met before requesting.",
              "10 trading days (7 profitable) required before each payout.",
            ]},
            { bold: "Limits", lines: [
              "Min payout: $500.",
              "Max payout: $1,500 ($25K) | $2,500 ($50K) | $3,000 ($100K) | $3,500 ($150K).",
            ]},
            { bold: "Processing", lines: [
              "Payout processing: under 4 hours average.",
              "Payout methods: Crypto, Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Equity touching or falling below intraday trailing drawdown — instant breach in real time.",
            ]},
            { bold: "Inactivity", lines: [
              "14-day inactivity — hard breach.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using EAs or bots.",
              "Hedging across accounts.",
              "Copying across different account types (PRO→Instant or PRO→Elite).",
              "Trades under 10 seconds.",
            ]},
          ]},
        ],
      },
      {
        name: "Ignite", badge: "Budget instant funding · 15% consistency · Soft DLL pause · Up to 10 accounts",
        params: [["Profit Target","5% per payout cycle"],["Max Loss Limit","$1K / $2K / $4K / $6K EOD Trailing"],["Daily Loss Limit","$500 / $1K / $2K / $3K"],["Consistency Rule","15% funded"],["Max Position Size","1 / 3 / 5 / 7 minis by size"],["Min Trading Days","None"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "One-time (sale)", values: ["~$131", "~$239", "~$338", "~$479"] },
          { label: "One-time (regular)", values: ["$218", "$398", "$563", "$799"] },
        ],
        evalTable: null,
        fundedTable: { title: "Ignite Funded", rows: [
          { label: "Payout target", values: ["5%", "5%", "5%", "5%"], color: null },
          { label: "Max drawdown", values: ["$1,000", "$2,000", "$4,000", "$6,000"], color: C.red },
          { label: "Daily loss limit", values: ["$500", "$1,000", "$2,000", "$3,000"], color: null },
          { label: "Max contracts", values: ["1", "3", "5", "7"], color: null },
          { label: "Consistency rule", values: ["15%", "15%", "15%", "15%"], color: null },
          { label: "Trading days", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Profitable days", values: ["None", "None", "None", "None"], color: C.textDim },
          { label: "Max payout", values: ["$500", "$1,000", "$1,500", "$2,000"], color: C.green },
          { label: "Profit split", values: ["90%", "90%", "90%", "90%"], color: C.green },
        ]},
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — 4% across all sizes.",
              "Locks at starting balance + $100 once profit threshold is met.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "DLL: $500 ($25K) / $1,000 ($50K) / $2,000 ($100K) / $3,000 ($150K).",
              "SOFT — pauses trading for the day, does NOT fail the account.",
            ]},
            { bold: "Consistency Rule", lines: [
              "15% rule: best day must be <15% of total profit — strictest consistency of any Top One product.",
              "Profit target: 5% per payout cycle, resets after each payout.",
            ]},
            { bold: "Account Limits", lines: [
              "No evaluation — instant funded access.",
              "Max 10 Ignite accounts simultaneously. No reset option.",
              "One-time fee — no monthly or activation charges.",
              "Copy trading: same size Ignite accounts only.",
            ]},
            { bold: "Unique Features", lines: [
              "No minimum trading days required.",
              "After 3 payouts → reviewed for live.",
              "Budget option — cheapest Top One product.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading ALLOWED — no restrictions.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No EAs, bots, or automated trading.",
              "10-second minimum trade duration.",
              "Copy trading across different plan types or sizes is prohibited.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions must be closed by 4:00 PM ET.",
              "No overnight or weekend positions.",
            ]},
            { bold: "Market Rules", lines: [
              "No hedging across accounts.",
              "Copy trading: same size Ignite accounts only.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "90/10 split.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Profit target: 5% per cycle (resets after each payout).",
              "15% consistency rule must be met before requesting.",
              "No minimum trading days required.",
            ]},
            { bold: "Limits", lines: [
              "Min payout: $250.",
              "Max payout: $500 ($25K) | $1,000 ($50K) | $1,500 ($100K) | $2,000 ($150K).",
            ]},
            { bold: "Processing", lines: [
              "Payout processing: under 4 hours average.",
              "Payout methods: Crypto, Riseworks.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Balance touching or falling below trailing drawdown — hard breach.",
            ]},
            { bold: "Inactivity", lines: [
              "14-day inactivity — hard breach.",
            ]},
            { bold: "Rule Violations", lines: [
              "Using EAs or bots.",
              "Hedging across accounts.",
              "Copy trading across different plan types or sizes.",
              "Trades under 10 seconds.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "tradeday", name: "TradeDay", tagline: "Chicago-based. 3 drawdown types. Day-one payouts. Up to 95% split.",
    bestFor: "Traders who want choice of EOD/Intraday/Static drawdown and day-one payouts.",
    logo: "https://www.google.com/s2/favicons?domain=tradeday.com&sz=64",
    founded: "2022", location: "Chicago, IL", regulated: false, website: "tradeday.com",
    drawdownType: "EOD Trailing / Intraday Trailing / Static (your choice)", profitSplit: "80→90→95%",
    newsTrading: false, overnightAllowed: false, hasDLL: false, maxFundedAccts: "3 Funded Sim (6 evals)",
    maxPayout: "No cap (EOD/Intraday)", daysToPass: "5 days",
    platforms: ["Tradovate", "NinjaTrader", "TradingView", "Jigsaw Daytradr", "Quantower", "TradeDayX"], trustpilotRating: 4.6,
    products: [
      {
        name: "EOD Account", badge: "EOD trailing · Most popular · Day-one payouts",
        params: [["Profit Target","$3K / $6K / $9K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","None"],["Consistency Rule","30% eval only"],["Max Position Size","5 / 10 / 15 minis (50 micros)"],["Min Trading Days","5 eval / None Funded Sim"]],
        sizes: ["50K", "100K", "150K"],
        pricing: [
          { label: "Monthly", values: ["$175", "$275", "$375"] },
          { label: "Activation fee", values: ["$139", "$139", "$139"] },
          { label: "Paid reset", values: ["$104", "$124", "$149"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (EOD)", values: ["$2,000", "$3,000", "$4,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["30%", "30%", "30%"], color: null },
            { label: "Min trading days", values: ["5", "5", "5"], color: null },
            { label: "News trading", values: ["Blocked", "Blocked", "Blocked"], color: C.red },
            { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Funded Sim",
          rows: [
            { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (EOD)", values: ["$2,000", "$3,000", "$4,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max payout", values: ["No cap", "No cap", "No cap"], color: C.green },
            { label: "Profit split", values: ["80→95%", "80→95%", "80→95%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown — recalculated at 4:00 PM CT market close based on end-of-day realized balance only.",
              "Intraday swings (up or down) do NOT affect the drawdown floor.",
              "Trails upward with new EOD highs, never moves down.",
              "Stops trailing once the floor reaches starting balance — locks permanently.",
            ]},
            { bold: "Consistency Rule", lines: [
              "30% rule on evaluation only — exceeding does NOT fail, just raises the effective profit target.",
              "No consistency rule on Funded Sim or Funded Live. No daily loss limit at any stage.",
            ]},
            { bold: "Account Limits", lines: [
              "Monthly subscription — rebills every 30 days. Auto-cancels after passing.",
              "Activation fee: $139 one-time (often $0 with promo codes). Must activate within 30 days or offer expires.",
              "Free reset included with each subscription renewal.",
              "Up to 6 evaluation accounts and 3 Funded Sim accounts simultaneously.",
            ]},
            { bold: "Unique Features", lines: [
              "After 3rd withdrawal: eligible for Funded Live (real capital trading).",
              "Funded Live: professional data fees apply (~$140/month per exchange). No fees on Funded Sim.",
              "Buffer = drawdown amount (e.g., $52,000 balance needed on $50K account before first payout).",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading prohibited — auto-liquidated 2 min before and 2 min after tier 1 data releases.",
              "Accidental breaches get warnings; repeated abuse loses account.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Third-party purchased bots/algos are forbidden. Custom-built strategies on supported platforms allowed.",
              "No manipulative trading, spoofing, layering, or order splitting.",
              "No VPN/VPS to mask location. No account sharing.",
            ]},
            { bold: "Position Rules", lines: [
              "Exit all positions 10 minutes before daily market close — auto-liquidated at 3:50 PM CT.",
              "No overnight or weekend positions.",
              "No more than 200 trades per day.",
            ]},
            { bold: "Market Rules", lines: [
              "No copy trading between TradeDay accounts. Trade copiers from personal external accounts into TradeDay are allowed.",
              "No trading within 2% of CME price limit on funded accounts.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "Progressive lifetime tiers: 80% (first $50K withdrawn) → 90% ($50K–$100K) → 95% (above $100K).",
              "Buffer zone withdrawals: 50/50 split.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Day-one payouts — eligible as soon as buffer is cleared.",
              "Buffer = drawdown amount (e.g., $52,000 balance needed on $50K EOD account).",
              "No minimum trading days required. No consistency rule required for payout.",
            ]},
            { bold: "Limits", lines: [
              "Minimum payout: $250. No restrictions on withdrawal frequency.",
            ]},
            { bold: "Processing", lines: [
              "Processed within 24 hours if requested before 5:30 PM CT (business days), via Riseworks.",
              "Payout methods: US bank wire (free) | International wire ($15) | L1 Crypto ($2.50 + gas) | L2 Crypto (free).",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Account balance hitting or exceeding the maximum drawdown — immediate termination.",
            ]},
            { bold: "Rule Violations", lines: [
              "Repeated abuse of permitted trading times or news windows.",
              "Trading within 2% of CME price limit on funded accounts.",
              "Using third-party purchased bots or algos.",
              "Manipulative trading patterns (spoofing, layering, sim exploitation).",
              "Account sharing.",
              "Copy trading between TradeDay accounts.",
            ]},
          ]},
        ],
      },
      {
        name: "Intraday Account", badge: "Intraday trailing · Cheapest · Real-time drawdown",
        params: [["Profit Target","$3K / $6K / $9K"],["Max Loss Limit","INTRADAY Trailing (real-time)"],["Daily Loss Limit","None"],["Consistency Rule","30% eval only"],["Max Position Size","5 / 10 / 15 minis (50 micros)"],["Min Trading Days","5 eval / None Funded Sim"]],
        sizes: ["50K", "100K", "150K"],
        pricing: [
          { label: "Monthly", values: ["$125", "$200", "$300"] },
          { label: "Activation fee", values: ["$139", "$139", "$139"] },
          { label: "Paid reset", values: ["$80", "$124", "$149"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (Intraday)", values: ["$2,000", "$3,000", "$4,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["30%", "30%", "30%"], color: null },
            { label: "Min trading days", values: ["5", "5", "5"], color: null },
            { label: "News trading", values: ["Blocked", "Blocked", "Blocked"], color: C.red },
            { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Funded Sim",
          rows: [
            { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (Intraday)", values: ["$2,000", "$3,000", "$4,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["5", "10", "15"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max payout", values: ["No cap", "No cap", "No cap"], color: C.green },
            { label: "Profit split", values: ["80→95%", "80→95%", "80→95%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "INTRADAY trailing drawdown — trails in real time based on highest equity INCLUDING unrealized P&L.",
              "Every new equity peak permanently raises the drawdown floor — even if the trade reverses and closes flat.",
              "Stops trailing once the floor reaches starting balance — locks permanently.",
              "Most aggressive drawdown type — not recommended for beginners.",
            ]},
            { bold: "Consistency Rule", lines: [
              "30% rule on evaluation only — exceeding does NOT fail, just raises the effective profit target.",
              "No consistency rule on Funded Sim or Funded Live. No daily loss limit.",
            ]},
            { bold: "Account Limits", lines: [
              "Monthly subscription — rebills every 30 days. Auto-cancels after passing.",
              "Activation fee: $139 one-time (often $0 with promo codes). Must activate within 30 days or offer expires.",
              "Free reset included with each subscription renewal. Cheapest TradeDay option — lowest monthly fee.",
              "Up to 6 evaluation accounts and 3 Funded Sim accounts simultaneously.",
            ]},
            { bold: "Unique Features", lines: [
              "After 3rd withdrawal: eligible for Funded Live (real capital trading).",
              "Same payout structure as EOD — day-one payouts, same split tiers, same buffer mechanics.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading prohibited — auto-liquidated 2 min before/after tier 1 releases.",
              "Repeated abuse loses account.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Third-party purchased bots/algos forbidden. Custom-built strategies allowed.",
              "No manipulative trading.",
              "No VPN/VPS to mask location. No account sharing.",
            ]},
            { bold: "Position Rules", lines: [
              "Exit all positions 10 minutes before market close — auto-liquidated at 3:50 PM CT.",
              "No overnight or weekend positions.",
              "No more than 200 trades per day.",
            ]},
            { bold: "Market Rules", lines: [
              "No copy trading between TradeDay accounts. Copiers from external accounts allowed.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "Progressive lifetime tiers: 80% (first $50K withdrawn) → 90% ($50K–$100K) → 95% (above $100K).",
              "Buffer zone withdrawals: 50/50 split.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Day-one payouts — eligible as soon as buffer is cleared.",
              "No minimum trading days required. No consistency rule required for payout.",
            ]},
            { bold: "Limits", lines: [
              "Minimum payout: $250. No restrictions on withdrawal frequency.",
            ]},
            { bold: "Processing", lines: [
              "Processed within 24 hours (business days), via Riseworks.",
              "Payout methods: US bank wire (free) | International wire ($15) | L1 Crypto ($2.50 + gas) | L2 Crypto (free).",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Account balance hitting or exceeding the intraday trailing drawdown — instant breach in real time.",
            ]},
            { bold: "Rule Violations", lines: [
              "Repeated abuse of news or trading time windows.",
              "Trading within 2% of CME price limit on funded accounts.",
              "Using third-party purchased bots.",
              "Manipulative trading patterns.",
              "Account sharing.",
              "Copy trading between TradeDay accounts.",
            ]},
          ]},
        ],
      },
      {
        name: "Static Account", badge: "Fixed drawdown · Tightest room · Lowest contracts",
        params: [["Profit Target","$1.5K / $2.5K / $3.75K"],["Max Loss Limit","Static (never moves)"],["Daily Loss Limit","None"],["Consistency Rule","30% eval only"],["Max Position Size","1 / 2 / 3 minis by size"],["Min Trading Days","5 eval / None Funded Sim"]],
        sizes: ["50K", "100K", "150K"],
        pricing: [
          { label: "Monthly", values: ["$165", "$250", "$350"] },
          { label: "Activation fee", values: ["$139", "$139", "$139"] },
          { label: "Paid reset", values: ["$104", "$124", "$149"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$2,500", "$3,750"], color: C.green },
            { label: "Max drawdown (Static)", values: ["$500", "$750", "$1,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["1", "2", "3"], color: null },
            { label: "Consistency rule", values: ["30%", "30%", "30%"], color: null },
            { label: "Min trading days", values: ["5", "5", "5"], color: null },
            { label: "News trading", values: ["Blocked", "Blocked", "Blocked"], color: C.red },
            { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "Funded Sim",
          rows: [
            { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max drawdown (Static)", values: ["$500", "$750", "$1,000"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["1", "2", "3"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max payout", values: ["No cap", "No cap", "No cap"], color: C.green },
            { label: "Profit split", values: ["80→95%", "80→95%", "80→95%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "STATIC drawdown — the floor is set at account opening and NEVER moves, regardless of profit.",
              "Extremely tight room — one bad mini trade can breach the account.",
              "Tightest drawdown of any TradeDay product. Lowest contract limits.",
            ]},
            { bold: "Consistency Rule", lines: [
              "30% rule on evaluation only — exceeding does NOT fail, just raises the effective profit target.",
              "No consistency rule on Funded Sim or Funded Live. No daily loss limit.",
            ]},
            { bold: "Account Limits", lines: [
              "Monthly subscription — rebills every 30 days. Auto-cancels after passing.",
              "Activation fee: $139 one-time (often $0 with promo codes). Must activate within 30 days or offer expires.",
              "Free reset included with each subscription renewal.",
              "Up to 6 evaluation accounts and 3 Funded Sim accounts simultaneously.",
            ]},
            { bold: "Unique Features", lines: [
              "Lower profit targets than EOD/Intraday — but tightest room to work with.",
              "After 3rd withdrawal: eligible for Funded Live (real capital trading).",
              "Same payout rules as other TradeDay accounts, but note the tighter account constraints.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading prohibited — auto-liquidated 2 min before/after tier 1 releases.",
              "Repeated abuse loses account.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "Third-party purchased bots/algos forbidden. Custom-built strategies allowed.",
              "No manipulative trading.",
              "No VPN/VPS to mask location. No account sharing.",
            ]},
            { bold: "Position Rules", lines: [
              "Exit all positions 10 minutes before market close — auto-liquidated at 3:50 PM CT.",
              "No overnight or weekend positions.",
              "No more than 200 trades per day.",
            ]},
            { bold: "Market Rules", lines: [
              "No copy trading between TradeDay accounts. Copiers from external accounts allowed.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "Progressive lifetime tiers: 80% (first $50K withdrawn) → 90% ($50K–$100K) → 95% (above $100K).",
              "Buffer zone withdrawals: 50/50 split.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Day-one payouts — eligible as soon as buffer is cleared.",
              "Buffer = drawdown amount (e.g., $50,500 balance needed on $50K Static account).",
              "No minimum trading days required. No consistency rule required for payout.",
            ]},
            { bold: "Limits", lines: [
              "Minimum payout: $250. No restrictions on withdrawal frequency.",
            ]},
            { bold: "Processing", lines: [
              "Processed within 24 hours (business days), via Riseworks.",
              "Payout methods: US bank wire (free) | International wire ($15) | L1 Crypto ($2.50 + gas) | L2 Crypto (free).",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Account balance hitting or exceeding the static drawdown floor — immediate termination.",
            ]},
            { bold: "Rule Violations", lines: [
              "Repeated abuse of news or trading time windows.",
              "Trading within 2% of CME price limit on funded accounts.",
              "Using third-party purchased bots.",
              "Manipulative trading patterns.",
              "Account sharing.",
              "Copy trading between TradeDay accounts.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "earn2trade", name: "Earn2Trade", tagline: "Oldest futures prop firm. Education-focused. Account growth plan.",
    bestFor: "Traders who want EOD drawdown, a structured growth path, and free monthly resets.",
    logo: "https://www.google.com/s2/favicons?domain=earn2trade.com&sz=64",
    founded: "2016", location: "Sheridan, WY", regulated: false, website: "earn2trade.com",
    drawdownType: "EOD Trailing (eval + LiveSim) / Intraday Trailing (Live)", profitSplit: "80%",
    newsTrading: true, overnightAllowed: false, hasDLL: true, maxFundedAccts: "Up to 3 funded (1 LiveSim active at a time)",
    maxPayout: "$4,000 (Gauntlet Mini)", daysToPass: "10 days",
    platforms: ["NinjaTrader", "Tradovate", "TradingView", "Finamark", "R|Trader", "R|Trader Pro", "Overcharts", "Bookmap", "Inside Edge Trader", "Investor/RT", "MotiveWave", "MultiCharts", "Photon", "Sierra Chart", "Quantower"], trustpilotRating: 4.7,
    products: [
      {
        name: "Gauntlet Mini", badge: "10 min days · No growth plan · Up to $200K",
        params: [["Profit Target","$3K / $6K / $9K / $11K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","$1.1K / $2.2K / $3.3K / $4.4K"],["Consistency Rule","30% eval only"],["Max Position Size","6 / 12 / 15 / 16 (progression ladder)"],["Min Trading Days","10 eval / None LiveSim"]],
        sizes: ["50K", "100K", "150K", "200K"],
        pricing: [
          { label: "Monthly", values: ["~$170", "~$315", "~$375", "~$550"] },
          { label: "Activation fee", values: ["$139", "$139", "$139", "$139"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$3,000", "$6,000", "$9,000", "$11,000"], color: C.green },
            { label: "Max drawdown", values: ["$2,000", "$3,500", "$4,500", "$6,000"], color: C.red },
            { label: "Daily loss limit", values: ["$1,100", "$2,200", "$3,300", "$4,400"], color: null },
            { label: "Max contracts", values: ["6", "12", "15", "16"], color: null },
            { label: "Consistency rule", values: ["30%", "30%", "30%", "30%"], color: null },
            { label: "Min trading days", values: ["10", "10", "10", "10"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "LiveSim / Live",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$2,000", "$3,500", "$4,500", "$6,000"], color: C.red },
            { label: "Daily loss limit", values: ["$1,100", "$2,200", "$3,300", "$4,400"], color: null },
            { label: "Max contracts", values: ["6", "12", "15", "16"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max payout", values: ["$5,000", "$5,000", "$5,000", "$5,000"], color: C.green },
            { label: "Profit split", values: ["80%", "80%", "80%", "80%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown on both eval and LiveSim — recalculated at end of each trading day based on closing balance.",
              "Drawdown floor moves up with new EOD highs and stops trailing once it reaches the starting balance.",
              "Open equity counts intraday — if an open position pushes balance below the MLL before the day ends, the account fails.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "DLL applies on both eval and LiveSim phases.",
              "Calculated on open equity — reaching or dipping below fails the account immediately.",
              "DLL can be removed on request once drawdown locks at starting balance.",
            ]},
            { bold: "Consistency Rule", lines: [
              "30% consistency rule on eval only — exceeding does NOT fail; it just requires further trading until that day drops below 30% of total profits.",
              "No consistency rule on LiveSim or Live.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Progression ladder — starts at a lower contract limit in LiveSim and scales up toward the stated maximum on Live.",
              "Self-regulated — exceeding the current ladder limit locks the account for the day (does not fail since March 2024).",
              "10 micros = 1 contract toward the progression ladder.",
            ]},
            { bold: "Account Limits", lines: [
              "Up to 3 funded accounts with prop firm partner; only 1 LiveSim active at a time.",
              "LiveSim capped at $4,000 total withdrawal — after that, transitions to a Live account.",
              "Live accounts use intraday trailing drawdown (trails in real time, not EOD).",
              "LiveSim or Live account cannot be reset if breached.",
            ]},
            { bold: "Unique Features", lines: [
              "Activation fee ($139) is deducted from first withdrawal — not paid upfront.",
              "Copy trading is NOT allowed across any accounts (including own accounts).",
              "5 consecutive unscheduled trading day absences on a LiveSim/Live account = termination.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading ALLOWED — no restrictions on either eval or LiveSim.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No automated trading or EAs — manual trading only.",
              "No copy trading (including own accounts).",
            ]},
            { bold: "Position Rules", lines: [
              "All positions and working orders must be closed between 3:50 PM CT and 5:00 PM CT.",
              "No overnight or weekend positions.",
              "Trading reopens at 5:00 PM CT.",
            ]},
            { bold: "Market Rules", lines: [
              "GF/HE/LE contracts must close by 1:05 PM CT.",
              "XC/XK/ZC/ZL/ZM/ZS/ZW must close during 7:45–8:30 AM CT and again 1:20–5:00 PM CT.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20 split — trader keeps 80%.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Withdrawals processed weekly on Wednesdays — submit request by prior Friday at 2:00 PM.",
              "No minimum trading days required for payout.",
            ]},
            { bold: "Limits", lines: [
              "Minimum net withdrawal: $100 (after 20% split and fees).",
              "LiveSim max total withdrawal: $4,000 (must reach $5,000 profit; 20% is deducted).",
              "Activation fee ($139) deducted from first LiveSim withdrawal.",
            ]},
            { bold: "Processing", lines: [
              "Withdrawal methods: Rise (US 1.5% / non-US $50) | Deel ($50) | Bayzat ($5–$40) | Direct Crypto (0.735%).",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Account balance reaching or dipping below the EOD drawdown threshold — including open equity intraday.",
            ]},
            { bold: "Inactivity", lines: [
              "5 consecutive unscheduled trading day absences on a LiveSim or Live account.",
            ]},
            { bold: "Rule Violations", lines: [
              "Reaching or dipping below the Daily Loss Limit.",
              "Holding positions past 3:50 PM CT.",
              "Using copy trading or trade copiers.",
            ]},
          ]},
        ],
      },
      {
        name: "Trader Career Path", badge: "10 min days · Growth to $400K · Free resets",
        params: [["Profit Target","$1.75K / $3K / $6K"],["Max Loss Limit","EOD Trailing"],["Daily Loss Limit","$550 / $1.1K / $2.2K"],["Consistency Rule","30% eval only"],["Max Position Size","3 / 6 / 12 (progression ladder)"],["Min Trading Days","10 eval / None LiveSim"]],
        sizes: ["25K", "50K", "100K"],
        pricing: [
          { label: "Monthly", values: ["~$150", "~$190", "~$350"] },
          { label: "Activation fee", values: ["$139", "$139", "$139"] },
          { label: "Reset", values: ["Free", "Free", "Free"] },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,750", "$3,000", "$6,000"], color: C.green },
            { label: "Max drawdown", values: ["$1,500", "$2,000", "$3,500"], color: C.red },
            { label: "Daily loss limit", values: ["$550", "$1,100", "$2,200"], color: null },
            { label: "Max contracts", values: ["3", "6", "12"], color: null },
            { label: "Consistency rule", values: ["30%", "30%", "30%"], color: null },
            { label: "Min trading days", values: ["10", "10", "10"], color: null },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None (rebills until pass or fail)", "None (rebills until pass or fail)", "None (rebills until pass or fail)"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "LiveSim / Live",
          rows: [
            { label: "Profit target", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$1,500", "$2,000", "$3,500"], color: C.red },
            { label: "Daily loss limit", values: ["$550", "$1,100", "$2,200"], color: null },
            { label: "Max contracts", values: ["3", "6", "12"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None"], color: C.textDim },
            { label: "Max payout", values: ["$1,750", "$3,000", "$6,000"], color: C.green },
            { label: "Profit split", values: ["80%", "80%", "80%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD trailing drawdown on both eval and LiveSim — recalculated at end of each trading day based on closing balance.",
              "Drawdown floor moves up with new EOD highs and stops trailing once it reaches the starting balance.",
              "Open equity counts intraday — if an open position pushes balance below the MLL before the day ends, the account fails.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "DLL applies on both eval and LiveSim phases.",
              "DLL can be removed on request once drawdown locks at starting balance.",
            ]},
            { bold: "Consistency Rule", lines: [
              "30% consistency rule on eval only — exceeding does NOT fail; it just requires further trading.",
              "No consistency rule on LiveSim or Live.",
            ]},
            { bold: "Contract Scaling", lines: [
              "Progression ladder — starts at lower limit in LiveSim, scales toward the stated maximum on Live.",
              "Self-regulated — exceeding the current ladder limit locks the account for the day.",
              "10 micros = 1 contract toward the progression ladder.",
            ]},
            { bold: "Account Limits", lines: [
              "Up to 3 funded accounts with prop firm partner; only 1 LiveSim active at a time.",
              "LiveSim max withdrawal = eval profit target minus 20%, then transitions to the next account tier.",
              "Live accounts use intraday trailing drawdown.",
              "LiveSim or Live account cannot be reset if breached.",
            ]},
            { bold: "Unique Features", lines: [
              "Growth plan: hit the profit target and withdraw → upgrade to the next funded account size.",
              "TCP path: $25K → $50K → $100K → $150K → $200K → $400K (path varies by starting size).",
              "Free reset included with every monthly rebill — resets accumulate but are forfeited on cancellation.",
              "Activation fee ($139) is deducted from first withdrawal — not paid upfront.",
              "Copy trading NOT allowed across any accounts.",
              "5 consecutive unscheduled trading day absences = termination.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading ALLOWED — no restrictions on either eval or LiveSim.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No automated trading or EAs — manual trading only.",
              "No copy trading (including own accounts).",
            ]},
            { bold: "Position Rules", lines: [
              "All positions and working orders must be closed between 3:50 PM CT and 5:00 PM CT.",
              "No overnight or weekend positions.",
              "Trading reopens at 5:00 PM CT.",
            ]},
            { bold: "Market Rules", lines: [
              "GF/HE/LE contracts must close by 1:05 PM CT.",
              "XC/XK/ZC/ZL/ZM/ZS/ZW must close during 7:45–8:30 AM CT and again 1:20–5:00 PM CT.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80/20 split — trader keeps 80%.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "Withdrawals processed weekly on Wednesdays — submit request by prior Friday at 2:00 PM.",
              "No minimum trading days required for payout.",
              "Withdrawing on LiveSim triggers the upgrade to the next funded account size.",
            ]},
            { bold: "Limits", lines: [
              "Minimum net withdrawal: $100 (after 20% split and fees).",
              "LiveSim max withdrawal = eval profit target minus 20%.",
              "After you withdraw, you move up to the next account size in the growth plan.",
              "Activation fee ($139) deducted from first LiveSim withdrawal.",
            ]},
            { bold: "Processing", lines: [
              "Withdrawal methods: Rise (US 1.5% / non-US $50) | Deel ($50) | Bayzat ($5–$40) | Direct Crypto (0.735%).",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Account balance reaching or dipping below the EOD drawdown threshold — including open equity intraday.",
            ]},
            { bold: "Inactivity", lines: [
              "5 consecutive unscheduled trading day absences on a LiveSim or Live account.",
            ]},
            { bold: "Rule Violations", lines: [
              "Reaching or dipping below the Daily Loss Limit.",
              "Holding positions past 3:50 PM CT.",
              "Using copy trading or trade copiers.",
            ]},
          ]},
        ],
      },
    ],
  },
  {
    id: "e8futures", name: "E8 Futures", tagline: "EOD Dynamic drawdown. News allowed. 35% best day on funded.",
    bestFor: "Traders who want EOD drawdown, news trading allowed, and on-demand payouts.",
    logo: "https://www.google.com/s2/favicons?domain=e8markets.com&sz=64",
    founded: "2024", location: "Dallas, TX", regulated: false, website: "e8markets.com",
    drawdownType: "EOD Dynamic", profitSplit: "80%",
    newsTrading: true, overnightAllowed: false, hasDLL: true, maxFundedAccts: "5 E8 Trader (unlimited eval)",
    maxPayout: "$5,250 (150K)", daysToPass: "1 day",
    platforms: ["Tradovate", "TradingView"], trustpilotRating: 4.5,
    products: [
      {
        name: "E8 Signature Futures", badge: "1-step · EOD Dynamic · 35% best day funded",
        params: [["Profit Target","$1.5K / $3K / $6K / $9K"],["Max Loss Limit","EOD Dynamic"],["Daily Loss Limit","None eval / Daily Pause funded (soft)"],["Consistency Rule","None eval / 35% best day E8 Trader"],["Max Position Size","2 / 4 / 8 / 12 contracts by size"],["Min Trading Days","None eval / 5 profitable days E8 Trader"]],
        sizes: ["25K", "50K", "100K", "150K"],
        pricing: [
          { label: "Sale price", values: ["$99", "$135", "$234", "$351"] },
          { label: "Regular price", values: ["$110", "$150", "$260", "$390"] },
          { label: "Activation fee", values: ["None", "None", "None", "None"], color: C.textDim },
        ],
        evalTable: {
          title: "Evaluation",
          rows: [
            { label: "Profit target", values: ["$1,500", "$3,000", "$6,000", "$9,000"], color: C.green },
            { label: "Max drawdown (EOD)", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
            { label: "Daily loss limit", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max contracts", values: ["2", "4", "8", "12"], color: null },
            { label: "Consistency rule", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Min trading days", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "News trading", values: ["Allowed", "Allowed", "Allowed", "Allowed"], color: C.green },
            { label: "Time limit", values: ["None", "None", "None", "None"], color: C.textDim },
          ],
        },
        fundedTable: {
          title: "E8 Trader (Sim Funded)",
          rows: [
            { label: "Profit target", values: ["None", "None", "None", "None"], color: C.textDim },
            { label: "Max drawdown", values: ["$1,000", "$2,000", "$3,000", "$4,500"], color: C.red },
            { label: "Daily Pause", values: ["$500", "$1,000", "$2,000", "$3,000"], color: null },
            { label: "Max contracts", values: ["2", "4", "8", "12"], color: null },
            { label: "Consistency rule", values: ["35%", "35%", "35%", "35%"], color: null },
            { label: "Profitable days", values: ["5", "5", "5", "5"], color: null },
            { label: "Max payout", values: ["$1,000 (tiered)", "$1,250 (tiered)", "$2,250 (tiered)", "$3,250 (tiered)"], color: C.green },
            { label: "Profit split", values: ["80%", "80%", "80%", "80%"], color: C.green },
          ],
        },
        sections: [
          { title: "Account Info", dot: C.textDim, items: [
            { bold: "Drawdown Behavior", lines: [
              "EOD Dynamic drawdown — recalculated at end of each trading day based on the highest achieved EOD balance.",
              "Drawdown floor moves up with new EOD highs and never moves down.",
              "Intraday dips that recover before market close do NOT affect the drawdown floor.",
            ]},
            { bold: "Daily Loss Limit", lines: [
              "No DLL during evaluation — trade freely intraday.",
              "Daily Pause on E8 Trader (funded) only — a soft limit that pauses trading for the day. Does NOT fail the account.",
            ]},
            { bold: "Consistency Rule", lines: [
              "35% Best Day rule applies on E8 Trader (funded) only — no single day's profit can exceed 35% of total account profits.",
              "Must satisfy the Best Day rule before each payout request.",
              "No consistency rule during evaluation.",
            ]},
            { bold: "Account Limits", lines: [
              "Unlimited evaluation accounts. Maximum 5 E8 Trader (funded) accounts simultaneously.",
              "7-day inactivity disables the account — can be reactivated (does not terminate).",
            ]},
            { bold: "Unique Features", lines: [
              "5 profitable days required between each payout — each day must have realized closed PnL of at least 0.3% of account size. Count resets after each payout.",
              "Payout caps increase progressively over the first 5 payouts — cap is lowest on 1st payout and rises each time.",
              "Tick scalping rule: at least 50% of profits must come from trades held longer than 10 seconds.",
              "Copy trading allowed across your own accounts (eval + E8 Trader + personal) — NOT for copying others.",
            ]},
          ]},
          { title: "Trading Restrictions", dot: C.orange, items: [
            { bold: "News Trading", lines: [
              "News trading ALLOWED — no restrictions on both eval and E8 Trader.",
            ]},
            { bold: "Prohibited Strategies", lines: [
              "No automated or semi-automated trading (bots, AI, HFT above 300 trades/day).",
              "No all-or-nothing trading or irresponsible risk behavior.",
              "No exploitation of gapped/illiquid markets or slow data feed abuse.",
            ]},
            { bold: "Position Rules", lines: [
              "All positions force-closed at 3:10 PM CT daily. Trading hours: 5:00 PM to 3:10 PM CT.",
              "No overnight or weekend positions.",
              "No hedging across accounts — even your own accounts.",
            ]},
            { bold: "Market Rules", lines: [
              "No holding positions within 2% of CME price limit.",
              "Must comply with all CME Group rules.",
              "No account sharing or management by others.",
            ]},
          ]},
          { title: "Payout Rules", dot: C.green, items: [
            { bold: "Profit Split", lines: [
              "80% profit split — trader keeps 80%.",
            ]},
            { bold: "Schedule & Requirements", lines: [
              "On-demand payouts — request anytime once all requirements are met.",
              "35% Best Day rule must be satisfied before requesting payout.",
              "5 profitable days required between each payout (count resets after each request).",
              "Profitable day = realized closed PnL of at least 0.3% of account size.",
            ]},
            { bold: "Limits", lines: [
              "Minimum payout: $100 (must request at least $125 at 80% split).",
              "Payout buffer: must leave an amount equal to the EOD drawdown in the account — not withdrawable.",
              "Payout caps by step — 25K: $1,000 / $1,000 / $1,250 / $1,250 / $1,500 (5+). 50K: $1,250 / $1,250 / $2,250 / $2,250 / $3,250 (5+). 100K: $2,250 / $2,250 / $3,250 / $3,250 / $4,250 (5+). 150K: $3,250 / $3,250 / $4,250 / $4,250 / $5,250 (5+).",
            ]},
            { bold: "Processing", lines: [
              "Payout methods: Riseworks, Plane.",
              "Typically processed within 24–48 hours.",
            ]},
          ]},
          { title: "Account Killers", dot: C.orange, items: [
            { bold: "Instant Breach", lines: [
              "Account balance exceeding the EOD Dynamic drawdown limit — account fails immediately.",
            ]},
            { bold: "Inactivity", lines: [
              "7-day inactivity disables the account (reactivation is possible, not automatic termination).",
            ]},
            { bold: "Rule Violations", lines: [
              "Using bots, AI, or any automated/semi-automated trading systems.",
              "Hedging across multiple accounts (including own accounts).",
              "Exploiting sim environment: gapped markets, slow feeds, or stray fills.",
              "All-or-nothing or irresponsible trading behavior.",
              "Account sharing or allowing others to manage the account.",
              "Violating CME Group rules.",
            ]},
          ]},
        ],
      },
    ],
  },
];
